<template>
  <v-container :fluid="true" :class="lgAndUp ? 'custom-wide-container' : ''">
    <div class="d-flex align-center mb-4">
      <v-btn
        color="primary"
        :loading="isLoading"
        @click="fetchResults"
        prepend-icon="mdi-refresh"
      >
        Ergebnisse aktualisieren
      </v-btn>

      <!-- Stauaufschlag nur anbieten, wenn für den Standort welche gepflegt
           sind. Ohne Konfiguration bleibt die Ansicht unverändert. -->
      <v-switch
        v-if="hasSurcharges"
        :model-value="surchargeEnabled"
        color="warning"
        density="compact"
        hide-details
        class="ml-6"
        label="Stauaufschläge berücksichtigen"
        @update:model-value="onSurchargeToggle"
      ></v-switch>
    </div>

    <!-- Dauerhaft sichtbare Kennzeichnung: solange der Schalter an ist, muss
         auf einen Blick erkennbar sein, dass die Zahlen Aufschläge enthalten. -->
    <v-alert
      v-if="surchargeActive"
      type="warning"
      variant="tonal"
      density="compact"
      class="mb-4"
      icon="mdi-traffic-light"
    >
      <strong>Die Ergebnisse enthalten Stauaufschläge.</strong> Die Wegzeiten
      sind je Herkunfts-PLZ und Verkehrsmittel erhöht; das wirkt sich auch auf
      das effektivste Verkehrsmittel und die beste Alternative aus. Der
      <strong>Mobscore bleibt bewusst ohne Aufschlag</strong>.
    </v-alert>

    <!-- First table -->
    <div class="mt-8">
      <h2>Einfache Pkw-Entfernung und CO₂-Ausstoß</h2>
      <p class="subtitle-1 green--text">
        ► Der einfache Arbeitsweg aller Mitarbeiter/-innen beträgt
        {{ formattedTotalDistance }} km
      </p>

      <v-table class="simple-distance-co2-table">
        <thead>
          <tr>
            <th rowspan="2" style="text-align: center">Zahl MA</th>
            <th colspan="4" style="text-align: center; font-size: 1.2rem">
              Pro Fahrt
            </th>
            <th colspan="2" style="text-align: center; font-size: 1.2rem">
              Pro Jahr
            </th>
          </tr>
          <tr>
            <th>Pkw-Entfernung Gesamt</th>
            <th>Pkw-Entfernung Durchschnitt</th>
            <th>CO₂-Ausstoß Gesamt</th>
            <th>CO₂-Ausstoß Durchschnitt</th>
            <th>CO₂-Ausstoß gesamt</th>
            <th>CO₂-Ausstoß Durchschnitt</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ employeeCount }} (von {{ totalEmployees }})*</td>
            <td>{{ formattedTotalDistance }} km</td>
            <td>{{ avgDistance }} km</td>
            <td>{{ formattedTotalCO2PerTrip }} kg</td>
            <td>{{ formattedAvgCO2PerTrip }} kg</td>
            <td>{{ totalCO2PerYear }} kg</td>
            <td>{{ avgCO2PerYear }} kg</td>
          </tr>
        </tbody>
      </v-table>

      <p class="caption mt-2">
        * Die Auswertung gilt nur für Mitarbeiter/-innen, die bis zu 80 km
        entfernt vom Arbeitsplatz wohnen (Pkw-Routing). Alle Werte beziehen sich
        auf eine einzelne Strecke.
      </p>
    </div>
    <br />
    <br />
    <v-divider :thickness="2"></v-divider>

    <!-- Second table -->
    <div class="mt-8">
      <h2>Mitarbeiter/-innenverteilung in km</h2>
      <p class="subtitle-1 green--text">
        ► {{ bikingDistancePercentage }} % der Mitarbeiter/-innen wohnen in
        Zweiradentfernung
      </p>

      <v-table class="distance-distribution-table custom-table-width">
        <thead>
          <tr>
            <th rowspan="2" style="text-align: center">Entfernung (km)</th>
            <th colspan="2" style="text-align: center">
              Anzahl Mitarbeiter/-innen
            </th>
            <th colspan="4" style="text-align: center">
              Mitarbeiter/-innen kumuliert
            </th>
          </tr>
          <tr>
            <th style="text-align: center">Pkw Entfernung</th>
            <th style="text-align: center">Pedelec Entfernung</th>
            <th style="text-align: center">Pkw Entfernung</th>
            <th style="text-align: center">%</th>
            <th style="text-align: center">Pedelec Entfernung</th>
            <th style="text-align: center">%</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in distanceDistribution" :key="row.distance">
            <td>{{ row.distance }}</td>
            <td>{{ row.carCount }}</td>
            <td>{{ row.pedelecCount || "" }}</td>
            <td>{{ row.carCumulative }}</td>
            <td>{{ row.carPercentage }} %</td>
            <td>{{ row.pedelecCumulative || "" }}</td>
            <td>
              {{ row.pedelecPercentage ? row.pedelecPercentage + " %" : "" }}
            </td>
          </tr>
        </tbody>
      </v-table>

      <!-- Icons legend -->
      <div class="d-flex align-center mt-4">
        <div class="speed-indicator mr-4">
          <v-icon>mdi-bike</v-icon>
          15 km/h
        </div>
        <div class="speed-indicator mr-4">
          <v-icon>mdi-bicycle-electric</v-icon>
          20 km/h
        </div>
        <div class="speed-indicator">
          <v-icon>mdi-moped</v-icon>
          bis 50 km/h
        </div>
      </div>
    </div>
    <br />
    <br />
    <v-divider :thickness="2"></v-divider>

    <!-- Third table -->
    <div class="mt-8">
      <h2>Wechselpotential {{ selectedComparison }}</h2>
      <p class="subtitle-1 green--text">
        ► Der ÖV ist meist deutlich langsamer, besonders durch lange Wartezeiten
      </p>
      <v-alert
        v-if="includesWaitingTime"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        Für Fixschichten enthält die angegebene Zeit zusätzlich die Wartezeit bis
        zum Arbeitsbeginn (Ankunft vor Schichtbeginn). Bei Gleitzeit-Modellen
        entfällt diese Wartezeit.
      </v-alert>

      <!-- Transport mode selector -->
      <v-select
        v-model="selectedComparison"
        :items="comparisonModes"
        item-title="text"
        item-value="value"
        label="Vergleichsmodus"
        class="mb-4 larger-select-text"
      ></v-select>

      <!-- Metric selector -->
      <v-select
        v-model="selectedMetric"
        :items="metricModes"
        item-title="text"
        item-value="value"
        label="Vergleichsmetrik"
        class="mb-4 larger-select-text"
      ></v-select>

      <!-- <div class="table-actions">
        <v-btn
          color="primary"
          :loading="isDownloading"
          @click="downloadTable"
          prepend-icon="mdi-download"
        >
          Download als Excel
        </v-btn>
      </div> -->

      <v-table class="comparison-matrix-table" :hover="false" variant="plain">
        <thead>
          <tr>
            <th colspan="14" style="text-align: center">
              {{ getMetricTitle + " - " + selectedComparison }}
            </th>
          </tr>
          <tr>
            <th>{{ getComparisonEmployeeCount }}**</th>
            <th colspan="13" style="text-align: center">
              {{ getSecondTransportMode }}
            </th>
          </tr>
          <tr>
            <th></th>
            <th>{{ selectedMetric }}</th>
            <th
              v-for="range in getColumnRanges"
              :key="range"
              style="text-align: center"
            >
              {{ range }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rowIndex) in currentMatrix"
            :key="rowIndex"
            class="border-b"
          >
            <td
              v-if="rowIndex === 0"
              :rowspan="currentMatrix.length"
              class="rotated-header"
            >
              <div class="rotated-text">{{ getFirstTransportMode }}</div>
            </td>
            <td style="font-size: 1.2rem">{{ row.mode }}</td>
            <td
              v-for="colIndex in 12"
              :key="colIndex"
              :class="getCellClass(Number(rowIndex), Number(colIndex) - 1)"
            >
              {{
                row.values && row.values[Number(colIndex) - 1] !== undefined
                  ? row.values[Number(colIndex) - 1]
                  : ""
              }}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr style="font-size: 1.2rem">
            <td colspan="14">
              Neutral: {{ neutralCount }} &nbsp;&nbsp;&nbsp; Verbesserung:
              {{ improvementCount }} &nbsp;&nbsp;&nbsp; Verschlechterung:
              {{ worseningCount }}
            </td>
          </tr>
        </tfoot>
      </v-table>
      <div v-if="filteredRecordsCount < totalRecordsCount" class="mt-2 caption">
        Hinweis: Es werden nur {{ filteredRecordsCount }} von
        {{ totalRecordsCount }} Adressen verglichen, da nur diese
        Vergleichsdaten für beide Verkehrsmittel haben und innerhalb der
        gültigen Entfernungsbereiche liegen ({{
          getTransportRangeInfo(selectedComparison)
        }}).
      </div>
      <p class="caption mt-2">
        ** Von insgesamt {{ totalEmployees }} Mitarbeiter/-innen am Standort
        haben {{ getComparisonEmployeeCount }} Mitarbeiter/-innen
        {{ minCountTransportMode }}-Verbindungen (zur ausgewählten Zeit) und
        werden beim Zeitvergleich berücksichtigt.
      </p>
    </div>
    <br />
    <br />
    <v-divider :thickness="2"></v-divider>

    <!-- Fourth table -->
    <div class="mt-8 mb-8">
      <h2>Optimale(s) Verkehrsmittel nach {{ selectedOptimalMetric }}</h2>

      <!-- Add metric selector -->
      <v-select
        v-model="selectedOptimalMetric"
        :items="optimalMetricModes"
        item-title="text"
        item-value="value"
        label="Optimierungsmetrik"
        class="mb-4 larger-select-text"
      ></v-select>

      <v-table class="optimal-transport-table">
        <thead>
          <tr>
            <th>Verkehrsmittel</th>
            <th v-for="range in distanceColumns" :key="range">
              {{ range }}
            </th>
            <th>Gesamt</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in optimalTransportMatrix" :key="row.mode">
            <td>{{ row.mode }}</td>
            <td v-for="(count, index) in row.counts" :key="index">
              {{ count || "" }}
            </td>
            <td>
              {{
                row.counts.reduce((sum, count) => sum + (count || 0), 0) || ""
              }}
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>

    <v-divider :thickness="2" class="mt-8"></v-divider>

    <!-- Fifth table: Best Alternative Analysis Table -->
    <div class="mt-8">
      <h2>
        Beste Alternative für {{ selectedBestAlternativeCurrentTransport }}
      </h2>
      <p class="subtitle-1 green--text">
        ► Analyse der besten Transportalternativen nach Entfernungsbereichen
      </p>

      <!-- Current Transport Selector -->
      <v-select
        v-model="selectedBestAlternativeCurrentTransport"
        :items="bestAlternativeTransportModes"
        item-title="text"
        item-value="value"
        label="Aktuelles Verkehrsmittel"
        class="mb-4 larger-select-text"
      ></v-select>

      <!-- Optimization Metric Selector -->
      <v-select
        v-model="selectedBestAlternativeMetric"
        :items="bestAlternativeMetricModes"
        item-title="text"
        item-value="value"
        label="Beste Alternative gemäß folgender Metrik"
        class="mb-4 larger-select-text"
      ></v-select>

      <!-- Constraint Settings -->
      <v-row class="mb-4">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="bestAlternativeConstraints.max_time_percentage"
            label="Max. Zeitaufschlag (%)"
            type="number"
            min="0"
            max="100"
            class="larger-select-text"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="bestAlternativeConstraints.max_additional_minutes"
            label="Max. zusätzliche Minuten"
            type="number"
            min="0"
            max="120"
            class="larger-select-text"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="bestAlternativeConstraints.metric_improvement_percentage"
            label="Verbesserung/Verschlechterung (%)"
            type="number"
            min="-100"
            max="100"
            class="larger-select-text"
            hint="Positiv: Verbesserung, Negativ: Verschlechterung"
            persistent-hint
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Additional Controls -->
      <v-row class="align-center">
        <v-col cols="12" md="4">
          <v-checkbox
            v-model="bestAlternativeConstraints.enforce_metric_constraints"
            label="Verbesserung/Verschlechterung berücksichtigen"
            class="larger-select-text"
            hint="Aktivieren um nur Alternativen mit der gewünschten Verbesserung/Verschlechterung zu zeigen"
            persistent-hint
          ></v-checkbox>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="bestAlternativeConstraints.max_legs"
            label="Max. Anzahl Umstiege (optional)"
            type="number"
            min="0"
            max="10"
            clearable
            class="larger-select-text"
            hint="Leer lassen für unbegrenzte Umstiege"
            persistent-hint
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Calculate and Export Buttons -->
      <div class="table-actions mb-4 d-flex gap-2">
        <v-btn
          color="primary"
          :loading="isCalculatingBestAlternative"
          @click="calculateBestAlternativeAnalysis"
          prepend-icon="mdi-calculator"
        >
          Beste Alternative berechnen
        </v-btn>
        <v-btn
          color="success"
          :loading="isExportingBestAlternative"
          :disabled="
            !templateStyleAnalysisResults ||
            templateStyleAnalysisResults.length === 0
          "
          @click="exportBestAlternativeToExcel"
          prepend-icon="mdi-file-excel"
        >
          Excel Export
        </v-btn>
        <v-btn
          color="info"
          :loading="isExportingBestAlternativeGeoJson"
          :disabled="
            !Array.isArray(bestAlternativePointResults) ||
            bestAlternativePointResults.length === 0
          "
          :title="getBestAlternativeGeoJsonTooltip()"
          @click="exportBestAlternativePointsGeoJson"
          prepend-icon="mdi-map-marker-path"
        >
          Punkte als GeoJSON
        </v-btn>
      </div>

      <!-- Combined Best Alternative Analysis Table -->
      <v-table
        class="best-alternative-combined-table full-width-table"
        variant="outlined"
        density="compact"
        style="border: 2px solid #333"
      >
        <thead>
          <!-- Main grouping header row -->
          <tr style="background-color: #f5f5f5">
            <th
              colspan="2"
              style="
                text-align: center;
                padding: 8px;
                border-right: 2px solid #333;
                border-bottom: 1px solid #333;
              "
            >
              <strong>{{ criteriaSummaryText }}</strong>
            </th>
            <th
              colspan="2"
              style="
                text-align: center;
                padding: 8px;
                background-color: #ffebee;
                border-right: 2px solid #333;
                border-bottom: 1px solid #333;
              "
            >
              <strong>keine Alternative</strong>
            </th>
            <th
              colspan="2"
              style="
                text-align: center;
                padding: 8px;
                background-color: #c8e6c9;
                border-right: 2px solid #333;
                border-bottom: 1px solid #333;
              "
            >
              <strong>alle Alternativen</strong>
            </th>
            <th
              :colspan="consideredAlternativeColumns.length"
              style="
                text-align: center;
                padding: 8px;
                background-color: #c8e6c9;
                border-bottom: 1px solid #333;
              "
            >
              <strong>Alternativen</strong>
            </th>
          </tr>
          <!-- Sub-header row with column names -->
          <tr style="background-color: #f5f5f5">
            <th
              style="
                text-align: center;
                width: 8%;
                border-right: 1px solid #aaa;
                border-bottom: 2px solid #333;
              "
            >
              <strong>Radius</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 8%;
                border-right: 2px solid #333;
                border-bottom: 2px solid #333;
              "
            >
              <strong>Anzahl Wohnorte</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 8%;
                background-color: #ffebee;
                border-right: 1px solid #aaa;
                border-bottom: 2px solid #333;
              "
            >
              <strong>absolut</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 8%;
                background-color: #ffebee;
                border-right: 2px solid #333;
                border-bottom: 2px solid #333;
              "
            >
              <strong>relativ</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 8%;
                background-color: #c8e6c9;
                border-right: 1px solid #aaa;
                border-bottom: 2px solid #333;
              "
            >
              <strong>absolut</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 8%;
                background-color: #c8e6c9;
                border-right: 2px solid #333;
                border-bottom: 2px solid #333;
              "
            >
              <strong>relativ</strong>
            </th>
            <th
              v-for="(transport, index) in consideredAlternativeColumns"
              :key="transport.key"
              :style="`text-align: center; background-color: #c8e6c9; border-bottom: 2px solid #333; ${index < consideredAlternativeColumns.length - 1 ? 'border-right: 1px solid #aaa;' : ''}`"
            >
              <strong>{{ transport.display }}</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Data rows for each distance range -->
          <tr
            v-for="row in templateStyleAnalysisResults"
            :key="row.distance_range"
            style="border-bottom: 1px solid #ddd"
          >
            <td
              style="
                text-align: center;
                font-weight: bold;
                border-right: 1px solid #aaa;
              "
            >
              {{ row.distance_range }}
            </td>
            <td style="text-align: center; border-right: 2px solid #333">
              {{ formatNumber(row.total_employees) }}
            </td>
            <td
              style="
                text-align: center;
                background-color: #ffebee;
                border-right: 1px solid #aaa;
              "
            >
              {{ formatNumber(row.employees_without_alternative) }}
            </td>
            <td
              style="
                text-align: center;
                background-color: #ffebee;
                border-right: 2px solid #333;
              "
            >
              {{ getNoAlternativePercentage(row) }} %
            </td>
            <td
              :style="
                getAlternativeCellStyle(
                  row.total_with_alternatives,
                  row.total_employees,
                )
              "
              style="text-align: center; border-right: 1px solid #aaa"
            >
              {{ formatNumber(row.total_with_alternatives) }}
            </td>
            <td
              :style="
                getAlternativeCellStyle(
                  row.total_with_alternatives,
                  row.total_employees,
                )
              "
              style="text-align: center; border-right: 2px solid #333"
            >
              {{ row.success_percentage }} %
            </td>
            <td
              v-for="(transport, index) in consideredAlternativeColumns"
              :key="`${row.distance_range}-${transport.key}`"
              :style="
                getTransportCellStyle(
                  row.transport_counts[transport.selector] || 0,
                  row.total_employees,
                ) +
                `; ${index < consideredAlternativeColumns.length - 1 ? 'border-right: 1px solid #aaa;' : ''}`
              "
              style="text-align: center"
            >
              {{ formatNumber(row.transport_counts[transport.selector] || 0) }}
            </td>
          </tr>
          <!-- Totals row (gesamt) -->
          <tr
            style="
              border-top: 2px solid #333;
              font-weight: bold;
              background-color: #f5f5f5;
            "
          >
            <td
              style="
                text-align: center;
                font-weight: bold;
                border-right: 1px solid #aaa;
              "
            >
              gesamt
            </td>
            <td style="text-align: center; border-right: 2px solid #333">
              {{ formatNumber(totalSummary.total_employees) }}
            </td>
            <td
              style="
                text-align: center;
                background-color: #ffebee;
                border-right: 1px solid #aaa;
              "
            >
              {{ formatNumber(totalSummary.employees_without_alternative) }}
            </td>
            <td
              style="
                text-align: center;
                background-color: #ffebee;
                border-right: 2px solid #333;
              "
            >
              {{ totalSummary.no_alternative_percentage }} %
            </td>
            <td
              :style="
                getAlternativeCellStyle(
                  totalSummary.total_with_alternatives,
                  totalSummary.total_employees,
                )
              "
              style="text-align: center; border-right: 1px solid #aaa"
            >
              {{ formatNumber(totalSummary.total_with_alternatives) }}
            </td>
            <td
              :style="
                getAlternativeCellStyle(
                  totalSummary.total_with_alternatives,
                  totalSummary.total_employees,
                )
              "
              style="text-align: center; border-right: 2px solid #333"
            >
              {{ totalSummary.success_percentage }} %
            </td>
            <td
              v-for="(transport, index) in consideredAlternativeColumns"
              :key="`total-${transport.key}`"
              :style="
                getTransportCellStyle(
                  totalSummary.transport_totals[transport.selector] || 0,
                  totalSummary.total_employees,
                ) +
                `; ${index < consideredAlternativeColumns.length - 1 ? 'border-right: 1px solid #aaa;' : ''}`
              "
              style="text-align: center"
            >
              {{
                formatNumber(
                  totalSummary.transport_totals[transport.selector] || 0,
                )
              }}
            </td>
          </tr>
          <!-- Percentage summary row -->
          <tr style="background-color: #fafafa">
            <td
              style="
                text-align: center;
                font-weight: bold;
                border-right: 1px solid #aaa;
              "
            >
              Anteil Verkehrsmittelart
            </td>
            <td style="border-right: 2px solid #333"></td>
            <td
              colspan="2"
              style="border-right: 2px solid #333; background-color: #ffebee"
            ></td>
            <td colspan="2" style="border-right: 2px solid #333"></td>
            <td
              v-for="(transport, index) in consideredAlternativeColumns"
              :key="`percent-${transport.key}`"
              :style="`text-align: center; ${index < consideredAlternativeColumns.length - 1 ? 'border-right: 1px solid #aaa;' : ''}`"
            >
              {{ getTotalTransportPercentage(transport.selector) }} %
            </td>
          </tr>
          <!-- Bundled percentages row -->
          <tr style="background-color: #fafafa">
            <td
              style="
                text-align: center;
                font-weight: bold;
                border-right: 1px solid #aaa;
              "
            >
              Gesamtanteil Verkehrsmittel
            </td>
            <td colspan="3" style="border-right: 2px solid #333"></td>
            <td
              colspan="2"
              style="
                text-align: center;
                font-weight: bold;
                border-right: 2px solid #333;
              "
            >
              {{ totalSummary.success_percentage }} %
            </td>
            <!-- Fahrrad + Pedelec bundled -->
            <td
              colspan="2"
              style="text-align: center; border-right: 1px solid #aaa"
            >
              {{ getBundledBikePercentage() }} %
            </td>
            <!-- ÖPNV + B&R + P&R bundled -->
            <td
              colspan="3"
              style="text-align: center; border-right: 1px solid #aaa"
            >
              {{ getBundledPublicTransportPercentage() }} %
            </td>
            <!-- zu Fuß stays separate -->
            <td style="text-align: center">
              {{ getTotalTransportPercentage("Zu Fuß") }} %
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>

    <v-divider :thickness="2" class="mt-8"></v-divider>

    <!-- Sixth table: Emissionen mit Bester Alternative -->
    <div
      v-if="
        Array.isArray(templateStyleAnalysisResults) &&
        templateStyleAnalysisResults.length > 0
      "
      class="mt-8 mb-8"
    >
      <h2>{{ comparisonMetricConfig.title }} mit Bester Alternative</h2>
      <p class="subtitle-1 green--text">
        ► Metrikvergleich nach Distanzbereichen auf Basis der besten
        Alternativen
      </p>
      <v-select
        v-model="selectedComparisonMetric"
        :items="comparisonMetricModes"
        item-title="text"
        item-value="value"
        label="Vergleichsmetrik für diese Tabelle"
        class="mb-4 larger-select-text"
      ></v-select>
      <div class="table-actions mb-4 d-flex gap-2">
        <v-btn
          color="success"
          :loading="isExportingBestAlternativeEmissions"
          :disabled="
            !Array.isArray(templateStyleAnalysisResults) ||
            templateStyleAnalysisResults.length === 0
          "
          @click="exportBestAlternativeEmissionsToExcel"
          prepend-icon="mdi-file-excel"
        >
          Excel Export Vergleich
        </v-btn>
      </div>

      <v-table
        class="best-alternative-emissions-table full-width-table"
        variant="outlined"
        density="compact"
        style="border: 2px solid #333"
      >
        <thead>
          <tr style="background-color: #f5f5f5">
            <th
              colspan="2"
              style="
                text-align: center;
                padding: 8px;
                border-right: 2px solid #333;
                border-bottom: 1px solid #333;
              "
            >
              <strong>{{ criteriaSummaryText }}</strong>
            </th>
            <th
              colspan="2"
              style="
                text-align: center;
                padding: 8px;
                background-color: #ffebee;
                border-right: 2px solid #333;
                border-bottom: 1px solid #333;
              "
            >
              <strong>keine Alternative</strong>
            </th>
            <th
              colspan="4"
              style="
                text-align: center;
                padding: 8px;
                background-color: #c8e6c9;
                border-right: 2px solid #333;
                border-bottom: 1px solid #333;
              "
            >
              <strong>{{ comparisonMetricGroupHeader }}</strong>
            </th>
            <th
              colspan="2"
              style="
                text-align: center;
                padding: 8px;
                border-bottom: 1px solid #333;
              "
            >
              <strong>{{ emissionsDifferenceGroupLabel }}</strong>
            </th>
          </tr>
          <tr style="background-color: #f5f5f5">
            <th
              style="
                text-align: center;
                width: 8%;
                border-right: 1px solid #aaa;
                border-bottom: 2px solid #333;
              "
            >
              <strong>Radius</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 8%;
                border-right: 2px solid #333;
                border-bottom: 2px solid #333;
              "
            >
              <strong>Anzahl Wohnorte</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 7%;
                background-color: #ffebee;
                border-right: 1px solid #aaa;
                border-bottom: 2px solid #333;
              "
            >
              <strong>absolut</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 7%;
                background-color: #ffebee;
                border-right: 2px solid #333;
                border-bottom: 2px solid #333;
              "
            >
              <strong>relativ</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 12%;
                white-space: normal;
                line-height: 1.2;
                border-right: 1px solid #aaa;
                border-bottom: 2px solid #333;
              "
            >
              <strong>{{ emissionsBaselineSubLabel }}</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 14%;
                white-space: normal;
                line-height: 1.2;
                border-right: 1px solid #aaa;
                border-bottom: 2px solid #333;
              "
            >
              <strong>gesamt<br />(mit/ohne Alternative)</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 11%;
                white-space: normal;
                line-height: 1.2;
                border-right: 1px solid #aaa;
                border-bottom: 2px solid #333;
              "
            >
              <strong>Beste<br />Alternative</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 11%;
                white-space: normal;
                line-height: 1.2;
                border-right: 2px solid #333;
                border-bottom: 2px solid #333;
              "
            >
              <strong>keine<br />Alternative</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 6%;
                border-right: 1px solid #aaa;
                border-bottom: 2px solid #333;
              "
            >
              <strong>absolut</strong>
            </th>
            <th
              style="
                text-align: center;
                width: 6%;
                border-bottom: 2px solid #333;
              "
            >
              <strong>relativ</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in emissionComparisonRows"
            :key="`emissions-${row.distance_range}`"
            style="border-bottom: 1px solid #ddd"
          >
            <td
              style="
                text-align: center;
                font-weight: bold;
                border-right: 1px solid #aaa;
              "
            >
              {{ row.distance_range }}
            </td>
            <td style="text-align: center; border-right: 2px solid #333">
              {{ formatNumber(row.total_employees) }}
            </td>
            <td
              style="
                text-align: center;
                background-color: #ffebee;
                border-right: 1px solid #aaa;
              "
            >
              {{ formatNumber(row.employees_without_alternative) }}
            </td>
            <td
              style="
                text-align: center;
                background-color: #ffebee;
                border-right: 2px solid #333;
              "
            >
              {{ row.no_alternative_percentage }} %
            </td>
            <td style="text-align: center; border-right: 1px solid #aaa">
              {{ formatComparisonMetricValue(row.baseline_value) }}
            </td>
            <td
              :style="
                getAlternativeCellStyle(
                  row.total_with_alternatives,
                  row.total_employees,
                )
              "
              style="text-align: center; border-right: 1px solid #aaa"
            >
              {{ formatComparisonMetricValue(row.combined_value) }}
            </td>
            <td style="text-align: center; border-right: 1px solid #aaa">
              {{ formatComparisonMetricValue(row.best_alternative_value) }}
            </td>
            <td style="text-align: center; border-right: 2px solid #333">
              {{ formatComparisonMetricValue(row.no_best_alternative_value) }}
            </td>
            <td
              :style="
                isDifferenceImprovement(row.difference_absolute)
                  ? 'text-align: center; color: #2e7d32; border-right: 1px solid #aaa;'
                  : 'text-align: center; color: #b71c1c; border-right: 1px solid #aaa;'
              "
            >
              {{ formatSignedComparisonMetricValue(row.difference_absolute) }}
            </td>
            <td
              :style="
                isDifferenceImprovement(row.difference_relative_percent)
                  ? 'text-align: center; color: #2e7d32;'
                  : 'text-align: center; color: #b71c1c;'
              "
            >
              {{ formatSignedPercentage(row.difference_relative_percent) }} %
            </td>
          </tr>

          <tr
            style="
              border-top: 2px solid #333;
              font-weight: bold;
              background-color: #f5f5f5;
            "
          >
            <td
              style="
                text-align: center;
                font-weight: bold;
                border-right: 1px solid #aaa;
              "
            >
              gesamt
            </td>
            <td style="text-align: center; border-right: 2px solid #333">
              {{ formatNumber(emissionComparisonTotals.total_employees) }}
            </td>
            <td
              style="
                text-align: center;
                background-color: #ffebee;
                border-right: 1px solid #aaa;
              "
            >
              {{
                formatNumber(
                  emissionComparisonTotals.employees_without_alternative,
                )
              }}
            </td>
            <td
              style="
                text-align: center;
                background-color: #ffebee;
                border-right: 2px solid #333;
              "
            >
              {{ emissionComparisonTotals.no_alternative_percentage }} %
            </td>
            <td style="text-align: center; border-right: 1px solid #aaa">
              {{
                formatComparisonMetricValue(
                  emissionComparisonTotals.baseline_value,
                )
              }}
            </td>
            <td style="text-align: center; border-right: 1px solid #aaa">
              {{
                formatComparisonMetricValue(
                  emissionComparisonTotals.combined_value,
                )
              }}
            </td>
            <td style="text-align: center; border-right: 1px solid #aaa">
              {{
                formatComparisonMetricValue(
                  emissionComparisonTotals.best_alternative_value,
                )
              }}
            </td>
            <td style="text-align: center; border-right: 2px solid #333">
              {{
                formatComparisonMetricValue(
                  emissionComparisonTotals.no_best_alternative_value,
                )
              }}
            </td>
            <td
              :style="
                isDifferenceImprovement(
                  emissionComparisonTotals.difference_absolute,
                )
                  ? 'text-align: center; color: #2e7d32; border-right: 1px solid #aaa;'
                  : 'text-align: center; color: #b71c1c; border-right: 1px solid #aaa;'
              "
            >
              {{
                formatSignedComparisonMetricValue(
                  emissionComparisonTotals.difference_absolute,
                )
              }}
            </td>
            <td
              :style="
                isDifferenceImprovement(
                  emissionComparisonTotals.difference_relative_percent,
                )
                  ? 'text-align: center; color: #2e7d32;'
                  : 'text-align: center; color: #b71c1c;'
              "
            >
              {{
                formatSignedPercentage(
                  emissionComparisonTotals.difference_relative_percent,
                )
              }}
              %
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>
  </v-container>

  <!-- Bottom margin for better page spacing -->
  <div style="margin-bottom: 4rem"></div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useDisplay } from "vuetify";
import { mapStores } from "pinia";
import ExcelJS from "exceljs";
import { useOfficeStore } from "../store/officeStore";
import { useCleverRouteImportStore } from "../store/cleverRouteImportStore";
import { useMapInputStore } from "../store/mapInputStore";
import { useMapOutputStore } from "../store/mapOutputStore";
import { usePlzSurchargeStore } from "../store/plzSurchargeStore";
import { resultsHaveSurcharge, surchargeFor } from "../utils/surcharge";

export default defineComponent({
  name: "TheResultsView",
  props: {
    // Optional callback injected from parent (e.g. TheUserView via <router-view>)
    // Not currently used here, but defining it avoids Vue's "extraneous non-props attributes"
    setResetHomeFunction: {
      type: Function,
      default: null,
    },
  },
  setup() {
    const { lgAndUp } = useDisplay();
    return { lgAndUp };
  },
  data() {
    return {
      isLoading: false,
      isDownloading: false,
      selectedMetric: "Entfernung",
      metricModes: [
        { text: "Entfernungsvergleich", value: "Entfernung" },
        { text: "Zeitvergleich", value: "Zeit" },
        { text: "CO₂-Vergleich", value: "CO₂" },
        { text: "Kostenvergleich", value: "Kosten" },
        { text: "Bewegungsvergleich", value: "Bewegung" },
      ],
      selectedComparison: "Pkw vs. ÖV",
      comparisonModes: [
        // Pkw comparisons
        { text: "Pkw vs. ÖV", value: "Pkw vs. ÖV" },
        { text: "Pkw vs. Bike&Ride", value: "Pkw vs. Bike&Ride" },
        { text: "Pkw vs. Park&Ride", value: "Pkw vs. Park&Ride" },
        { text: "Pkw vs. Pedelec", value: "Pkw vs. Pedelec" },
        { text: "Pkw vs. Fahrrad", value: "Pkw vs. Fahrrad" },
        { text: "Pkw vs. Bahnreakt.", value: "Pkw vs. Bahnreakt." },

        // ÖV comparisons
        { text: "ÖV vs. Pkw", value: "ÖV vs. Pkw" },
        { text: "ÖV vs. Bike&Ride", value: "ÖV vs. Bike&Ride" },
        { text: "ÖV vs. Park&Ride", value: "ÖV vs. Park&Ride" },
        { text: "ÖV vs. Pedelec", value: "ÖV vs. Pedelec" },
        { text: "ÖV vs. Fahrrad", value: "ÖV vs. Fahrrad" },
        { text: "ÖV vs. Bahnreakt.", value: "ÖV vs. Bahnreakt." },

        // Bike&Ride comparisons
        { text: "Bike&Ride vs. Pkw", value: "Bike&Ride vs. Pkw" },
        { text: "Bike&Ride vs. ÖV", value: "Bike&Ride vs. ÖV" },
        { text: "Bike&Ride vs. Park&Ride", value: "Bike&Ride vs. Park&Ride" },
        { text: "Bike&Ride vs. Pedelec", value: "Bike&Ride vs. Pedelec" },
        { text: "Bike&Ride vs. Fahrrad", value: "Bike&Ride vs. Fahrrad" },
        { text: "Bike&Ride vs. Bahnreakt.", value: "Bike&Ride vs. Bahnreakt." },

        // Park&Ride comparisons
        { text: "Park&Ride vs. Pkw", value: "Park&Ride vs. Pkw" },
        { text: "Park&Ride vs. ÖV", value: "Park&Ride vs. ÖV" },
        { text: "Park&Ride vs. Bike&Ride", value: "Park&Ride vs. Bike&Ride" },
        { text: "Park&Ride vs. Pedelec", value: "Park&Ride vs. Pedelec" },
        { text: "Park&Ride vs. Fahrrad", value: "Park&Ride vs. Fahrrad" },
        { text: "Park&Ride vs. Bahnreakt.", value: "Park&Ride vs. Bahnreakt." },

        // Pedelec comparisons
        { text: "Pedelec vs. Pkw", value: "Pedelec vs. Pkw" },
        { text: "Pedelec vs. ÖV", value: "Pedelec vs. ÖV" },
        { text: "Pedelec vs. Bike&Ride", value: "Pedelec vs. Bike&Ride" },
        { text: "Pedelec vs. Park&Ride", value: "Pedelec vs. Park&Ride" },
        { text: "Pedelec vs. Fahrrad", value: "Pedelec vs. Fahrrad" },
        { text: "Pedelec vs. Bahnreakt.", value: "Pedelec vs. Bahnreakt." },

        // Fahrrad comparisons
        { text: "Fahrrad vs. Pkw", value: "Fahrrad vs. Pkw" },
        { text: "Fahrrad vs. ÖV", value: "Fahrrad vs. ÖV" },
        { text: "Fahrrad vs. Bike&Ride", value: "Fahrrad vs. Bike&Ride" },
        { text: "Fahrrad vs. Park&Ride", value: "Fahrrad vs. Park&Ride" },
        { text: "Fahrrad vs. Pedelec", value: "Fahrrad vs. Pedelec" },
        { text: "Fahrrad vs. Bahnreakt.", value: "Fahrrad vs. Bahnreakt." },

        // Bahnreaktivierung & Ride comparisons
        { text: "Bahnreakt. vs. Pkw", value: "Bahnreakt. vs. Pkw" },
        { text: "Bahnreakt. vs. ÖV", value: "Bahnreakt. vs. ÖV" },
        { text: "Bahnreakt. vs. Bike&Ride", value: "Bahnreakt. vs. Bike&Ride" },
        { text: "Bahnreakt. vs. Park&Ride", value: "Bahnreakt. vs. Park&Ride" },
        { text: "Bahnreakt. vs. Pedelec", value: "Bahnreakt. vs. Pedelec" },
        { text: "Bahnreakt. vs. Fahrrad", value: "Bahnreakt. vs. Fahrrad" },

        // Park & Ride & Bike comparisons
        { text: "P&R&B vs. Pkw", value: "P&R&B vs. Pkw" },
        { text: "P&R&B vs. ÖV", value: "P&R&B vs. ÖV" },
        { text: "P&R&B vs. Bike&Ride", value: "P&R&B vs. Bike&Ride" },
        { text: "P&R&B vs. Park&Ride", value: "P&R&B vs. Park&Ride" },
        { text: "P&R&B vs. Pedelec", value: "P&R&B vs. Pedelec" },
        { text: "P&R&B vs. Fahrrad", value: "P&R&B vs. Fahrrad" },
      ],
      distanceColumns: [
        "0 - 5",
        "5 - 10",
        "10 - 15",
        "15 - 20",
        "20 - 30",
        "30 - 40",
        "40 - 50",
        "50 - 60",
        "60 - 80",
        "80 - 100",
        "100 - 120",
        ">120",
      ],
      selectedOptimalMetric: "Entfernung",
      optimalMetricModes: [
        { text: "Gesamt-Nutzen", value: "Entfernung" },
        { text: "Zeit", value: "Zeit" },
        { text: "CO₂", value: "CO₂" },
        { text: "Kosten", value: "Kosten" },
        { text: "Bewegung", value: "Bewegung" },
      ],
      transportModes: [
        "PKW",
        "E_Auto",
        "OeV",
        "Park_and_Ride",
        "Bike_and_Ride",
        "Pedelec",
        "Fahrrad",
        "Fuss_Weg",
      ],
      transportModeDisplayNames: {
        PKW: "Pkw",
        E_Auto: "E-Auto",
        OeV: "ÖV",
        Park_and_Ride: "Park & Ride",
        Bike_and_Ride: "Bike & Ride",
        Pedelec: "Pedelec",
        Fahrrad: "Fahrrad",
        Fuss_Weg: "Fußweg",
      },
      // Best Alternative Analysis
      selectedBestAlternativeCurrentTransport: "Auto",
      bestAlternativeTransportModes: [
        { text: "Pkw", value: "Auto" },
        { text: "E-Auto", value: "E-Auto" },
        { text: "ÖV", value: "ÖV" },
        { text: "Fahrrad", value: "Fahrrad" },
        { text: "Pedelec", value: "Pedelec" },
        { text: "Zu Fuß", value: "Zu Fuß" },
        { text: "Taxi", value: "Taxi" },
        { text: "Park & Ride", value: "Park & Ride" },
        { text: "Bike & Ride", value: "Bike & Ride" },
        { text: "e-Scooter", value: "e-Scooter" },
        { text: "Carsharing", value: "Carsharing" },
        { text: "Bahnreaktivierung & Ride", value: "Bahnreaktivierung & Ride" },
        { text: "Park & Ride & Bike", value: "Park & Ride & Bike" },
      ],
      selectedBestAlternativeMetric: "time",
      bestAlternativeMetricModes: [
        { text: "Zeit (Min.)", value: "time" },
        { text: "Kosten (Euro)", value: "cost" },
        { text: "CO₂ (kg)", value: "co2" },
        { text: "Sport (Schritte)", value: "sport" },
      ],
      selectedComparisonMetric: "co2",
      comparisonMetricModes: [
        { text: "CO₂ (kg)", value: "co2" },
        { text: "Zeit (Min.)", value: "time" },
        { text: "Kosten (Euro)", value: "cost" },
        { text: "Sport (Schritte)", value: "sport" },
        { text: "Distanz (km)", value: "distance" },
      ],
      bestAlternativeConstraints: {
        max_time_percentage: 20,
        max_additional_minutes: 20,
        metric_improvement_percentage: 50,
        enforce_metric_constraints: false,
        max_legs: undefined,
      },
      useSurveyTransportBaselineForEmissions: false,
      isCalculatingBestAlternative: false,
      bestAlternativeCalculated: false,
      // Reactive data properties for async calculation results
      bestAlternativeAnalysisResults: [],
      bestAlternativePointResults: [],
      templateStyleAnalysisResults: [],
      isCalculatingAnalysisData: false,
      isExportingBestAlternative: false,
      isExportingBestAlternativeEmissions: false,
      isExportingBestAlternativeGeoJson: false,
    };
  },

  computed: {
    ...mapStores(
      useCleverRouteImportStore,
      useOfficeStore,
      useMapInputStore,
      usePlzSurchargeStore,
    ),
    crImportResults() {
      // @ts-ignore
      return this.cleverRouteImportStore.cr_import_results;
    },
    // Für diesen Standort sind Stauaufschläge gepflegt - erst dann wird der
    // Schalter überhaupt angeboten.
    hasSurcharges(): boolean {
      return this.plzSurchargeStore.hasSurcharges;
    },
    surchargeEnabled(): boolean {
      return this.plzSurchargeStore.enabled;
    },
    // Schalter an UND die geladenen Ergebnisse tragen tatsächlich einen
    // Aufschlag. Sonst stünde die Warnung auch über Zahlen, die sich gar nicht
    // geändert haben - etwa wenn nur eine PLZ ohne Beschäftigte gepflegt ist.
    surchargeActive(): boolean {
      if (!this.surchargeEnabled) return false;
      return resultsHaveSurcharge(this.crImportResults);
    },
    // true, wenn mind. ein Ergebnis aus einer Fixschicht stammt und die
    // angezeigte Zeit damit die Wartezeit bis Arbeitsbeginn enthält
    includesWaitingTime(): boolean {
      if (!this.crImportResults?.length) return false;
      return this.crImportResults.some(
        (record) => Number(record.Zeit_enthaelt_Wartezeit) === 1,
      );
    },
    uniqueAddresses() {
      return this.cleverRouteImportStore.uniqueAddresses;
    },
    // Total number of employees (from uniqueAddresses)
    totalEmployees(): number {
      if (!this.crImportResults?.length) return 0;

      return this.crImportResults.filter(
        (record) => record.MA_ID && parseFloat(record.MA_ID) > 0,
      ).length;
    },
    // Number of employees with valid value for PKW_Entfernung
    employeeCountPkw(): number {
      if (!this.crImportResults?.length) return 0;

      return this.crImportResults.filter(
        (record) =>
          record.MA_ID &&
          parseFloat(record.MA_ID) > 0 &&
          record.PKW_Entfernung &&
          parseFloat(record.PKW_Entfernung) > 0,
      ).length;
    },
    // Number of employees with valid value for Pedelec_Entfernung
    employeeCountPedelec(): number {
      if (!this.crImportResults?.length) return 0;

      return this.crImportResults.filter(
        (record) =>
          record.MA_ID &&
          parseFloat(record.MA_ID) > 0 &&
          record.Pedelec_Entfernung &&
          parseFloat(record.Pedelec_Entfernung) > 0,
      ).length;
    },
    // Number of employees with valid value for Fahrrad_Entfernung
    employeeCountFahrrad(): number {
      if (!this.crImportResults?.length) return 0;

      return this.crImportResults.filter(
        (record) =>
          record.MA_ID &&
          parseFloat(record.MA_ID) > 0 &&
          record.Fahrrad_Entfernung &&
          parseFloat(record.Fahrrad_Entfernung) > 0,
      ).length;
    },
    // Number of employees with valid value for OeV_Entfernung
    employeeCountOeV(): number {
      if (!this.crImportResults?.length) return 0;

      return this.crImportResults.filter(
        (record) =>
          record.MA_ID &&
          parseFloat(record.MA_ID) > 0 &&
          record.OeV_Entfernung &&
          parseFloat(record.OeV_Entfernung) > 0,
      ).length;
    },
    // Number of employees with valid value for OeV_Fahrrad_Entfernung
    employeeCountBikeAndRide(): number {
      if (!this.crImportResults?.length) return 0;

      return this.crImportResults.filter(
        (record) =>
          record.MA_ID &&
          parseFloat(record.MA_ID) > 0 &&
          record.OeV_Fahrrad_Entfernung &&
          parseFloat(record.OeV_Fahrrad_Entfernung) > 0,
      ).length;
    },
    // Number of employees with valid value for Auto_OeV_Entfernung
    employeeCountParkAndRide(): number {
      if (!this.crImportResults?.length) return 0;

      return this.crImportResults.filter(
        (record) =>
          record.MA_ID &&
          parseFloat(record.MA_ID) > 0 &&
          record.Auto_OeV_Entfernung &&
          parseFloat(record.Auto_OeV_Entfernung) > 0,
      ).length;
    },
    // Number of employees with valid value for PKW_Entfernung
    employeeCount(): number {
      if (!this.crImportResults?.length) return 0;

      return this.crImportResults.filter(
        (record) =>
          // Check for valid MA_ID
          record.MA_ID &&
          parseFloat(record.MA_ID) > 0 &&
          // Check for valid PKW_Entfernung within range (0-80km)
          record.PKW_Entfernung &&
          parseFloat(record.PKW_Entfernung) > 0 &&
          parseFloat(record.PKW_Entfernung) <= 80,
      ).length;
    },
    // Get employee count for first transport mode
    getEmployeeCountForFirstMode(): number {
      const firstMode = this.getFirstTransportMode;

      switch (firstMode) {
        case "Pkw":
          return this.employeeCountPkw;
        case "ÖV":
          return this.employeeCountOeV;
        case "Bike&Ride":
          return this.employeeCountBikeAndRide;
        case "Park&Ride":
          return this.employeeCountParkAndRide;
        case "Pedelec":
          return this.employeeCountPedelec;
        case "Fahrrad":
          return this.employeeCountFahrrad;
        default:
          return 0;
      }
    },
    // Get employee count for second transport mode
    getEmployeeCountForSecondMode(): number {
      const secondMode = this.getSecondTransportMode;

      switch (secondMode) {
        case "Pkw":
          return this.employeeCountPkw;
        case "ÖV":
          return this.employeeCountOeV;
        case "Bike&Ride":
          return this.employeeCountBikeAndRide;
        case "Park&Ride":
          return this.employeeCountParkAndRide;
        case "Pedelec":
          return this.employeeCountPedelec;
        case "Fahrrad":
          return this.employeeCountFahrrad;
        default:
          return 0;
      }
    },
    // Get the minimum employee count between the two compared transport modes
    getComparisonEmployeeCount(): number {
      return Math.min(
        this.getEmployeeCountForFirstMode,
        this.getEmployeeCountForSecondMode,
      );
    },
    // Determine which transport mode has the minimum count
    minCountTransportMode(): string {
      if (
        this.getEmployeeCountForFirstMode <= this.getEmployeeCountForSecondMode
      ) {
        return this.getFirstTransportMode;
      } else {
        return this.getSecondTransportMode;
      }
    },
    totalDistance(): number {
      if (!this.crImportResults?.length) return 0;
      return this.crImportResults.reduce((sum, record) => {
        const distance = parseFloat(record.PKW_Entfernung) || 0;
        return distance > 0 && distance <= 80 ? sum + distance : sum;
      }, 0);
    },
    // Format the total distance to display with 1 decimal place
    formattedTotalDistance(): string {
      return this.totalDistance.toLocaleString("de-DE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    },
    avgDistance(): string {
      if (!this.employeeCount) return "0,0";
      const average = this.totalDistance / this.employeeCount;
      return average.toLocaleString("de-DE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }); // Format to 1 decimal place
    },
    totalCO2PerTrip(): number {
      if (!this.crImportResults?.length) return 0;

      const totalGrams = this.crImportResults.reduce((sum, record) => {
        const distance = parseFloat(record.PKW_Entfernung) || 0;
        // Only include records with PKW distance <= 80 km
        if (distance > 80) return sum;

        const co2 = parseFloat(record.PKW_CO2) || 0;
        return sum + co2;
      }, 0);
      // Convert from grams to kilograms
      const totalCO2PerTrip = totalGrams / 1000;
      // console.log("totalCO2PerTrip (≤80km): ", totalCO2PerTrip);
      return totalCO2PerTrip;
    },
    // Formatted version with one decimal place
    formattedTotalCO2PerTrip(): string {
      return this.totalCO2PerTrip.toLocaleString("de-DE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    },
    avgCO2PerTrip(): number {
      if (!this.employeeCount) return 0;
      //const avgCO2PerTrip = this.totalCO2PerTrip / this.employeeCount;
      //console.log("avgCO2PerTrip: ", avgCO2PerTrip);
      return this.totalCO2PerTrip / this.employeeCount;
    },
    formattedAvgCO2PerTrip(): string {
      return this.avgCO2PerTrip.toLocaleString("de-DE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    },
    totalCO2PerYear(): string {
      const TRIPS_PER_DAY = 2;
      const WORK_DAYS_PER_YEAR = 220;
      const value = this.totalCO2PerTrip * TRIPS_PER_DAY * WORK_DAYS_PER_YEAR;
      return value.toLocaleString("de-DE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    },
    // Format to whole number since it's a large value
    // formattedTotalCO2PerYear(): string {
    //   return Math.round(this.totalCO2PerYear).toLocaleString();
    // },
    avgCO2PerYear(): string {
      const TRIPS_PER_DAY = 2;
      const WORK_DAYS_PER_YEAR = 220;
      const value = this.avgCO2PerTrip * TRIPS_PER_DAY * WORK_DAYS_PER_YEAR;
      return value.toLocaleString("de-DE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    },
    bikingDistancePercentage(): string {
      if (!this.crImportResults?.length || !this.totalEmployees) return "0";

      const employeesWithinBikeDistance = this.crImportResults.reduce(
        (count, record) => {
          const distance = parseFloat(record.PKW_Entfernung) || 0;
          // Count employees with distance ≤ 12 km
          return distance > 0 && distance <= 12 ? count + 1 : count;
        },
        0,
      );

      const percentage =
        (employeesWithinBikeDistance / this.totalEmployees) * 100;
      return Math.round(percentage).toString();
    },
    distanceDistribution() {
      if (!this.crImportResults?.length) return [];

      // Initialize counters for each range (typed so pedelecCount can be optional)
      type RangeCounts = { carCount: number; pedelecCount?: number };
      const ranges: Record<string, RangeCounts> = {
        "0-2": { carCount: 0, pedelecCount: 0 },
        "2-5": { carCount: 0, pedelecCount: 0 },
        "5-10": { carCount: 0, pedelecCount: 0 },
        "10-15": { carCount: 0, pedelecCount: 0 },
        "15-20": { carCount: 0 },
        "20-30": { carCount: 0 },
        "30-40": { carCount: 0 },
        "40-50": { carCount: 0 },
        "50-80": { carCount: 0 },
      };

      // Count employees in each range
      this.crImportResults.forEach((record) => {
        // Handle car distances
        const carDistance = parseFloat(record.PKW_Entfernung) || 0;
        if (carDistance > 0) {
          if (carDistance <= 2) ranges["0-2"].carCount++;
          else if (carDistance <= 5) ranges["2-5"].carCount++;
          else if (carDistance <= 10) ranges["5-10"].carCount++;
          else if (carDistance <= 15) ranges["10-15"].carCount++;
          else if (carDistance <= 20) ranges["15-20"].carCount++;
          else if (carDistance <= 30) ranges["20-30"].carCount++;
          else if (carDistance <= 40) ranges["30-40"].carCount++;
          else if (carDistance <= 50) ranges["40-50"].carCount++;
          else if (carDistance <= 80) ranges["50-80"].carCount++;
        }

        // Handle pedelec distances (only up to 12km)
        const pedelecDistance = parseFloat(record.Pedelec_Entfernung) || 0;
        if (pedelecDistance > 0) {
          if (pedelecDistance <= 2) ranges["0-2"].pedelecCount++;
          else if (pedelecDistance <= 5) ranges["2-5"].pedelecCount++;
          else if (pedelecDistance <= 10) ranges["5-10"].pedelecCount++;
          else if (pedelecDistance <= 15) ranges["10-15"].pedelecCount++;
        }
      });

      // Calculate cumulative counts and percentages
      let carCumulative = 0;
      let pedelecCumulative = 0;

      return Object.entries(ranges).map(([distance, data]) => {
        carCumulative += data.carCount;
        if (data.pedelecCount !== undefined) {
          pedelecCumulative += data.pedelecCount;
        }

        return {
          distance,
          carCount: data.carCount,
          pedelecCount: data.pedelecCount ?? null,
          carCumulative,
          carPercentage: Math.round(
            (carCumulative / this.totalEmployees) * 100,
          ),
          pedelecCumulative:
            data.pedelecCount !== undefined ? pedelecCumulative : null,
          pedelecPercentage:
            data.pedelecCount !== undefined
              ? Math.round((pedelecCumulative / this.totalEmployees) * 100)
              : null,
        };
      });
    },
    filteredRecordsCount() {
      return this.getFilteredRecordsForComparison().length;
    },
    totalRecordsCount() {
      return this.crImportResults?.length || 0;
    },
    parameters() {
      return this.cleverRouteImportStore.parameters;
    },
    distanceMatrix() {
      if (!this.crImportResults?.length) return [];

      // Initialize matrix for distance comparison
      const matrix = [
        { mode: "0-5", values: Array(12).fill(0) },
        { mode: "5-10", values: Array(12).fill(0) },
        { mode: "10-15", values: Array(12).fill(0) },
        { mode: "15-20", values: Array(12).fill(0) },
        { mode: "20-30", values: Array(12).fill(0) },
        { mode: "30-40", values: Array(12).fill(0) },
        { mode: "40-50", values: Array(12).fill(0) },
        { mode: "50-60", values: Array(12).fill(0) },
        { mode: "60-80", values: Array(12).fill(0) },
        { mode: "80-100", values: Array(12).fill(0) },
        { mode: "100-120", values: Array(12).fill(0) },
        { mode: ">120", values: Array(12).fill(0) },
      ];

      this.crImportResults.forEach((record) => {
        let distance1, distance2;

        // Get distances based on selected comparison
        switch (this.selectedComparison) {
          case "Pkw vs. ÖV":
            distance1 = parseFloat(record.PKW_Entfernung) || 0;
            // console.log(distance1);
            distance2 = parseFloat(record.OeV_Entfernung) || 0;
            // console.log(distance2);
            break;
          case "Pkw vs. Bike&Ride":
            distance1 = parseFloat(record.PKW_Entfernung) || 0;
            // console.log(distance1);
            distance2 = parseFloat(record.OeV_Fahrrad_Entfernung) || 0;
            // console.log(distance2);
            break;
          case "Pkw vs. Park&Ride":
            distance1 = parseFloat(record.PKW_Entfernung) || 0;
            // console.log(distance1);
            distance2 = parseFloat(record.Auto_OeV_Entfernung) || 0;
            // console.log(distance2);
            break;
          case "Pkw vs. Pedelec":
            distance1 = parseFloat(record.PKW_Entfernung) || 0;
            // console.log(distance1);
            distance2 = parseFloat(record.Pedelec_Entfernung) || 0;
            // console.log(distance2);
            break;
          case "Pkw vs. Fahrrad":
            distance1 = parseFloat(record.PKW_Entfernung) || 0;
            // console.log(distance1);
            distance2 = parseFloat(record.Fahrrad_Entfernung) || 0;
            // console.log(distance2);
            break;
          case "ÖV vs. Pkw":
            distance1 = parseFloat(record.OeV_Entfernung) || 0;
            // console.log(distance1);
            distance2 = parseFloat(record.PKW_Entfernung) || 0;
            // console.log(distance2);
            break;
          case "ÖV vs. Bike&Ride":
            distance1 = parseFloat(record.OeV_Entfernung) || 0;
            distance2 = parseFloat(record.OeV_Fahrrad_Entfernung) || 0;
            break;
          case "ÖV vs. Park&Ride":
            distance1 = parseFloat(record.OeV_Entfernung) || 0;
            distance2 = parseFloat(record.Auto_OeV_Entfernung) || 0;
            break;
          case "ÖV vs. Pedelec":
            distance1 = parseFloat(record.OeV_Entfernung) || 0;
            distance2 = parseFloat(record.Pedelec_Entfernung) || 0;
            break;
          case "ÖV vs. Fahrrad":
            distance1 = parseFloat(record.OeV_Entfernung) || 0;
            distance2 = parseFloat(record.Fahrrad_Entfernung) || 0;
            break;
          case "Bike&Ride vs. Pkw":
            distance1 = parseFloat(record.OeV_Fahrrad_Entfernung) || 0;
            distance2 = parseFloat(record.PKW_Entfernung) || 0;
            break;
          case "Bike&Ride vs. ÖV":
            distance1 = parseFloat(record.OeV_Fahrrad_Entfernung) || 0;
            distance2 = parseFloat(record.OeV_Entfernung) || 0;
            break;
          case "Bike&Ride vs. Park&Ride":
            distance1 = parseFloat(record.OeV_Fahrrad_Entfernung) || 0;
            distance2 = parseFloat(record.Auto_OeV_Entfernung) || 0;
            break;
          case "Bike&Ride vs. Pedelec":
            distance1 = parseFloat(record.OeV_Fahrrad_Entfernung) || 0;
            distance2 = parseFloat(record.Pedelec_Entfernung) || 0;
            break;
          case "Bike&Ride vs. Fahrrad":
            distance1 = parseFloat(record.OeV_Fahrrad_Entfernung) || 0;
            distance2 = parseFloat(record.Fahrrad_Entfernung) || 0;
            break;
          case "Park&Ride vs. Pkw":
            distance1 = parseFloat(record.Auto_OeV_Entfernung) || 0;
            distance2 = parseFloat(record.PKW_Entfernung) || 0;
            break;
          case "Pedelec vs. Pkw":
            distance1 = parseFloat(record.Pedelec_Entfernung) || 0;
            distance2 = parseFloat(record.PKW_Entfernung) || 0;
            break;
          case "Fahrrad vs. Pkw":
            distance1 = parseFloat(record.Fahrrad_Entfernung) || 0;
            distance2 = parseFloat(record.PKW_Entfernung) || 0;
            break;
        }

        if (distance1 > 0 && distance2 > 0) {
          // Find row index based on distance1
          let rowIndex = this.getDistanceIndex(distance1);

          // Find column index based on distance2
          let colIndex = this.getDistanceIndex(distance2);

          // Increment counter if valid indices found
          if (rowIndex >= 0 && colIndex >= 0) {
            matrix[rowIndex].values[colIndex]++;
          }
        }
      });

      // Convert zero values to null for display
      return matrix.map((row) => ({
        mode: row.mode,
        values: row.values.map((v) => (v === 0 ? null : v)),
      }));
    },
    timeMatrix() {
      if (!this.crImportResults?.length) return [];
      // Get filtered records based on transport mode ranges from parameters
      const filteredRecords = this.getFilteredRecordsForComparison();
      if (!filteredRecords.length) return [];

      // Initialize matrix for time comparison
      const matrix = [
        { mode: "0-5", values: Array(12).fill(0) },
        { mode: "5-10", values: Array(12).fill(0) },
        { mode: "10-15", values: Array(12).fill(0) },
        { mode: "15-20", values: Array(12).fill(0) },
        { mode: "20-30", values: Array(12).fill(0) },
        { mode: "30-40", values: Array(12).fill(0) },
        { mode: "40-50", values: Array(12).fill(0) },
        { mode: "50-60", values: Array(12).fill(0) },
        { mode: "60-80", values: Array(12).fill(0) },
        { mode: "80-100", values: Array(12).fill(0) },
        { mode: "100-120", values: Array(12).fill(0) },
        { mode: ">120", values: Array(12).fill(0) },
      ];

      filteredRecords.forEach((record) => {
        let time1, time2;

        switch (this.selectedComparison) {
          case "Pkw vs. ÖV":
            time1 = parseFloat(record.PKW_Zeit) || 0;
            time2 = parseFloat(record.OeV_Zeit) || 0;
            break;
          case "Pkw vs. Bike&Ride":
            time1 = parseFloat(record.PKW_Zeit) || 0;
            time2 = parseFloat(record.OeV_Fahrrad_Zeit) || 0;
            break;
          case "Pkw vs. Park&Ride":
            time1 = parseFloat(record.PKW_Zeit) || 0;
            time2 = parseFloat(record.Auto_OeV_Zeit) || 0;
            break;
          case "Pkw vs. Pedelec":
            time1 = parseFloat(record.PKW_Zeit) || 0;
            time2 = parseFloat(record.Pedelec_Zeit) || 0;
            break;
          case "Pkw vs. Fahrrad":
            time1 = parseFloat(record.PKW_Zeit) || 0;
            time2 = parseFloat(record.Fahrrad_Zeit) || 0;
            break;
          case "ÖV vs. Pkw":
            time1 = parseFloat(record.OeV_Zeit) || 0;
            time2 = parseFloat(record.PKW_Zeit) || 0;
            break;
          case "ÖV vs. Bike&Ride":
            time1 = parseFloat(record.OeV_Zeit) || 0;
            time2 = parseFloat(record.OeV_Fahrrad_Zeit) || 0;
            break;
          case "ÖV vs. Park&Ride":
            time1 = parseFloat(record.OeV_Zeit) || 0;
            time2 = parseFloat(record.Auto_OeV_Zeit) || 0;
            break;
          case "ÖV vs. Pedelec":
            time1 = parseFloat(record.OeV_Zeit) || 0;
            time2 = parseFloat(record.Pedelec_Zeit) || 0;
            break;
          case "ÖV vs. Fahrrad":
            time1 = parseFloat(record.OeV_Zeit) || 0;
            time2 = parseFloat(record.Fahrrad_Zeit) || 0;
            break;
          case "Bike&Ride vs. Pkw":
            time1 = parseFloat(record.OeV_Fahrrad_Zeit) || 0;
            time2 = parseFloat(record.PKW_Zeit) || 0;
            break;
          case "Bike&Ride vs. ÖV":
            time1 = parseFloat(record.OeV_Fahrrad_Zeit) || 0;
            time2 = parseFloat(record.OeV_Zeit) || 0;
            break;
          case "Bike&Ride vs. Park&Ride":
            time1 = parseFloat(record.OeV_Fahrrad_Zeit) || 0;
            time2 = parseFloat(record.Auto_OeV_Zeit) || 0;
            break;
          case "Bike&Ride vs. Pedelec":
            time1 = parseFloat(record.OeV_Fahrrad_Zeit) || 0;
            time2 = parseFloat(record.Pedelec_Zeit) || 0;
            break;
          case "Bike&Ride vs. Fahrrad":
            time1 = parseFloat(record.OeV_Fahrrad_Zeit) || 0;
            time2 = parseFloat(record.Fahrrad_Zeit) || 0;
            break;
          case "Park&Ride vs. Pkw":
            time1 = parseFloat(record.Auto_OeV_Zeit) || 0;
            time2 = parseFloat(record.PKW_Zeit) || 0;
            break;
          case "Pedelec vs. Pkw":
            time1 = parseFloat(record.Pedelec_Zeit) || 0;
            time2 = parseFloat(record.PKW_Zeit) || 0;
            break;
          case "Fahrrad vs. Pkw":
            time1 = parseFloat(record.Fahrrad_Zeit) || 0;
            time2 = parseFloat(record.PKW_Zeit) || 0;
            break;
        }

        if (time1 > 0 && time2 > 0) {
          // Find row index based on time1 (comparison mode1 time)
          let rowIndex = this.getTimeIndex(time1);

          // Find column index based on time2 (comparison mode2 time)
          let colIndex = this.getTimeIndex(time2);

          // Increment counter
          if (rowIndex >= 0 && colIndex >= 0) {
            matrix[rowIndex].values[colIndex]++;
          }
        }
      });

      // Convert zero values to null for display
      return matrix.map((row) => ({
        mode: row.mode,
        values: row.values.map((v) => (v === 0 ? null : v)),
      }));
    },
    getMetricTitle() {
      switch (this.selectedMetric) {
        case "Entfernung":
          return "Entfernungsvergleich in Kilometern";
        case "Zeit":
          return "Zeitvergleich in Minuten";
        case "CO₂":
          return "CO₂-Vergleich in kg";
        case "Kosten":
          return "Kostenvergleich in €";
        case "Bewegung":
          return "Bewegungsvergleich in kcal (höher ist besser)";
        default:
          return "";
      }
    },
    getFirstTransportMode(): string {
      // Extract the first transport mode from the comparison
      const parts = this.selectedComparison.split(" vs. ");
      return parts[0];
    },
    getSecondTransportMode(): string {
      // Extract the second transport mode from the comparison
      const parts = this.selectedComparison.split(" vs. ");
      return parts[1];
    },
    currentMatrix() {
      switch (this.selectedMetric) {
        case "Entfernung":
          return this.distanceMatrix;
        case "Zeit":
          return this.timeMatrix;
        case "CO₂":
          return this.co2Matrix;
        case "Kosten":
          return this.costMatrix;
        case "Bewegung":
          return this.sportMatrix;
        default:
          return [];
      }
    },
    co2Matrix() {
      if (!this.crImportResults?.length) return [];
      // Get filtered records based on transport mode ranges from parameters
      const filteredRecords = this.getFilteredRecordsForComparison();
      if (!filteredRecords.length) return [];

      // Initialize matrix for CO2 comparison (in kg)
      const matrix = [
        { mode: "0-0.5", values: Array(12).fill(0) },
        { mode: "0.5-1", values: Array(12).fill(0) },
        { mode: "1-2", values: Array(12).fill(0) },
        { mode: "2-3", values: Array(12).fill(0) },
        { mode: "3-4", values: Array(12).fill(0) },
        { mode: "4-6", values: Array(12).fill(0) },
        { mode: "6-8", values: Array(12).fill(0) },
        { mode: "8-10", values: Array(12).fill(0) },
        { mode: "10-12", values: Array(12).fill(0) },
        { mode: "12-14", values: Array(12).fill(0) },
        { mode: "14-15", values: Array(12).fill(0) },
        { mode: ">15", values: Array(12).fill(0) },
      ];

      filteredRecords.forEach((record) => {
        let co21, co22;

        switch (this.selectedComparison) {
          case "Pkw vs. ÖV":
            co21 = parseFloat(record.PKW_CO2) || 0;
            co22 = parseFloat(record.OeV_CO2) || 0;
            break;
          case "Pkw vs. Bike&Ride":
            co21 = parseFloat(record.PKW_CO2) || 0;
            co22 = parseFloat(record.OeV_Fahrrad_CO2) || 0;
            break;
          case "Pkw vs. Park&Ride":
            co21 = parseFloat(record.PKW_CO2) || 0;
            co22 = parseFloat(record.Auto_OeV_CO2) || 0;
            break;
          case "Pkw vs. Pedelec":
            co21 = parseFloat(record.PKW_CO2) || 0;
            co22 = parseFloat(record.Pedelec_CO2) || 0;
            break;
          case "Pkw vs. Fahrrad":
            co21 = parseFloat(record.PKW_CO2) || 0;
            co22 = parseFloat(record.Fahrrad_CO2) || 0;
            break;
          case "ÖV vs. Pkw":
            co21 = parseFloat(record.OeV_CO2) || 0;
            co22 = parseFloat(record.PKW_CO2) || 0;
            break;
          case "ÖV vs. Bike&Ride":
            co21 = parseFloat(record.OeV_CO2) || 0;
            co22 = parseFloat(record.OeV_Fahrrad_CO2) || 0;
            break;
          case "ÖV vs. Park&Ride":
            co21 = parseFloat(record.OeV_CO2) || 0;
            co22 = parseFloat(record.Auto_OeV_CO2) || 0;
            break;
          case "ÖV vs. Pedelec":
            co21 = parseFloat(record.OeV_CO2) || 0;
            co22 = parseFloat(record.Pedelec_CO2) || 0;
            break;
          case "ÖV vs. Fahrrad":
            co21 = parseFloat(record.OeV_CO2) || 0;
            co22 = parseFloat(record.Fahrrad_CO2) || 0;
            break;
          case "Bike&Ride vs. Pkw":
            co21 = parseFloat(record.OeV_Fahrrad_CO2) || 0;
            co22 = parseFloat(record.PKW_CO2) || 0;
            break;
          case "Bike&Ride vs. ÖV":
            co21 = parseFloat(record.OeV_Fahrrad_CO2) || 0;
            co22 = parseFloat(record.OeV_CO2) || 0;
            break;
          case "Bike&Ride vs. Park&Ride":
            co21 = parseFloat(record.OeV_Fahrrad_CO2) || 0;
            co22 = parseFloat(record.Auto_OeV_CO2) || 0;
            break;
          case "Bike&Ride vs. Pedelec":
            co21 = parseFloat(record.OeV_Fahrrad_CO2) || 0;
            co22 = parseFloat(record.Pedelec_CO2) || 0;
            break;
          case "Bike&Ride vs. Fahrrad":
            co21 = parseFloat(record.OeV_Fahrrad_CO2) || 0;
            co22 = parseFloat(record.Fahrrad_CO2) || 0;
            break;
          case "Park&Ride vs. Pkw":
            co21 = parseFloat(record.Auto_OeV_CO2) || 0;
            co22 = parseFloat(record.PKW_CO2) || 0;
            break;
          case "Pedelec vs. Pkw":
            co21 = parseFloat(record.Pedelec_CO2) || 0;
            co22 = parseFloat(record.PKW_CO2) || 0;
            break;
          case "Fahrrad vs. Pkw":
            co21 = parseFloat(record.Fahrrad_CO2) || 0;
            co22 = parseFloat(record.PKW_CO2) || 0;
            break;
        }

        // Convert from grams to kg
        co21 = co21 / 1000;
        co22 = co22 / 1000;

        if (co21 > 0 && co22 > 0) {
          // Find row index based on co21 (comparison mode1 co2)
          let rowIndex = this.getCO2Index(co21);

          // Find column index based on co22 (comparison mode2 co2)
          let colIndex = this.getCO2Index(co22);

          // Increment counter if valid indices found
          if (rowIndex >= 0 && colIndex >= 0) {
            matrix[rowIndex].values[colIndex]++;
          }
        }
      });

      // Convert zero values to null for display
      return matrix.map((row) => ({
        mode: row.mode,
        values: row.values.map((v) => (v === 0 ? null : v)),
      }));
    },
    costMatrix() {
      if (!this.crImportResults?.length) return [];
      // Get filtered records based on transport mode ranges from parameters
      const filteredRecords = this.getFilteredRecordsForComparison();
      if (!filteredRecords.length) return [];

      // Initialize matrix for cost comparison (in €)
      const matrix = [
        { mode: "0-2", values: Array(12).fill(0) },
        { mode: "2-4", values: Array(12).fill(0) },
        { mode: "4-6", values: Array(12).fill(0) },
        { mode: "6-8", values: Array(12).fill(0) },
        { mode: "8-10", values: Array(12).fill(0) },
        { mode: "10-12", values: Array(12).fill(0) },
        { mode: "12-14", values: Array(12).fill(0) },
        { mode: "14-16", values: Array(12).fill(0) },
        { mode: "16-18", values: Array(12).fill(0) },
        { mode: "18-20", values: Array(12).fill(0) },
        { mode: "20-25", values: Array(12).fill(0) },
        { mode: ">25", values: Array(12).fill(0) },
      ];

      filteredRecords.forEach((record) => {
        let cost1, cost2;

        switch (this.selectedComparison) {
          case "Pkw vs. ÖV":
            cost1 = parseFloat(record.PKW_Kosten) || 0;
            cost2 = parseFloat(record.OeV_Kosten) || 0;
            break;
          case "Pkw vs. Bike&Ride":
            cost1 = parseFloat(record.PKW_Kosten) || 0;
            cost2 = parseFloat(record.OeV_Fahrrad_Kosten) || 0;
            break;
          case "Pkw vs. Park&Ride":
            cost1 = parseFloat(record.PKW_Kosten) || 0;
            cost2 = parseFloat(record.Auto_OeV_Kosten) || 0;
            break;
          case "Pkw vs. Pedelec":
            cost1 = parseFloat(record.PKW_Kosten) || 0;
            cost2 = parseFloat(record.Pedelec_Kosten) || 0;
            break;
          case "Pkw vs. Fahrrad":
            cost1 = parseFloat(record.PKW_Kosten) || 0;
            cost2 = parseFloat(record.Fahrrad_Kosten) || 0;
            break;
          case "ÖV vs. Pkw":
            cost1 = parseFloat(record.OeV_Kosten) || 0;
            cost2 = parseFloat(record.PKW_Kosten) || 0;
            break;
          case "ÖV vs. Bike&Ride":
            cost1 = parseFloat(record.OeV_Kosten) || 0;
            cost2 = parseFloat(record.OeV_Fahrrad_Kosten) || 0;
            break;
          case "ÖV vs. Park&Ride":
            cost1 = parseFloat(record.OeV_Kosten) || 0;
            cost2 = parseFloat(record.Auto_OeV_Kosten) || 0;
            break;
          case "ÖV vs. Pedelec":
            cost1 = parseFloat(record.OeV_Kosten) || 0;
            cost2 = parseFloat(record.Pedelec_Kosten) || 0;
            break;
          case "ÖV vs. Fahrrad":
            cost1 = parseFloat(record.OeV_Kosten) || 0;
            cost2 = parseFloat(record.Fahrrad_Kosten) || 0;
            break;
          case "Bike&Ride vs. Pkw":
            cost1 = parseFloat(record.OeV_Fahrrad_Kosten) || 0;
            cost2 = parseFloat(record.PKW_Kosten) || 0;
            break;
          case "Bike&Ride vs. ÖV":
            cost1 = parseFloat(record.OeV_Fahrrad_Kosten) || 0;
            cost2 = parseFloat(record.OeV_Kosten) || 0;
            break;
          case "Bike&Ride vs. Park&Ride":
            cost1 = parseFloat(record.OeV_Fahrrad_Kosten) || 0;
            cost2 = parseFloat(record.Auto_OeV_Kosten) || 0;
            break;
          case "Bike&Ride vs. Pedelec":
            cost1 = parseFloat(record.OeV_Fahrrad_Kosten) || 0;
            cost2 = parseFloat(record.Pedelec_Kosten) || 0;
            break;
          case "Bike&Ride vs. Fahrrad":
            cost1 = parseFloat(record.OeV_Fahrrad_Kosten) || 0;
            cost2 = parseFloat(record.Fahrrad_Kosten) || 0;
            break;
          case "Park&Ride vs. Pkw":
            cost1 = parseFloat(record.Auto_OeV_Kosten) || 0;
            cost2 = parseFloat(record.PKW_Kosten) || 0;
            break;
          case "Pedelec vs. Pkw":
            cost1 = parseFloat(record.Pedelec_Kosten) || 0;
            cost2 = parseFloat(record.PKW_Kosten) || 0;
            break;
          case "Fahrrad vs. Pkw":
            cost1 = parseFloat(record.Fahrrad_Kosten) || 0;
            cost2 = parseFloat(record.PKW_Kosten) || 0;
            break;
        }

        if (cost1 > 0 && cost2 > 0) {
          // Find row index based on cost1 (comparison mode1 cost)
          let rowIndex = this.getCostIndex(cost1);

          // Find column index based on cost2 (comparison mode2 cost)
          let colIndex = this.getCostIndex(cost2);

          // Increment counter if valid indices found
          if (rowIndex >= 0 && colIndex >= 0) {
            matrix[rowIndex].values[colIndex]++;
          }
        }
      });

      // Convert zero values to null for display
      return matrix.map((row) => ({
        mode: row.mode,
        values: row.values.map((v) => (v === 0 ? null : v)),
      }));
    },
    sportMatrix() {
      if (!this.crImportResults?.length) return [];
      // Get filtered records based on transport mode ranges from parameters
      const filteredRecords = this.getFilteredRecordsForComparison();
      if (!filteredRecords.length) return [];

      // Initialize matrix for movement/sport comparison (in kcal)
      const matrix = [
        { mode: ">600", values: Array(12).fill(0) },
        { mode: "500-600", values: Array(12).fill(0) },
        { mode: "450-500", values: Array(12).fill(0) },
        { mode: "400-450", values: Array(12).fill(0) },
        { mode: "350-400", values: Array(12).fill(0) },
        { mode: "300-350", values: Array(12).fill(0) },
        { mode: "250-300", values: Array(12).fill(0) },
        { mode: "200-250", values: Array(12).fill(0) },
        { mode: "150-200", values: Array(12).fill(0) },
        { mode: "100-150", values: Array(12).fill(0) },
        { mode: "50-100", values: Array(12).fill(0) },
        { mode: "0-50", values: Array(12).fill(0) },
      ];

      filteredRecords.forEach((record) => {
        let sport1, sport2;

        switch (this.selectedComparison) {
          case "Pkw vs. ÖV":
            sport1 = parseFloat(record.PKW_Bewegung) || 0;
            sport2 = parseFloat(record.OeV_Bewegung) || 0;
            break;
          case "Pkw vs. Bike&Ride":
            sport1 = parseFloat(record.PKW_Bewegung) || 0;
            sport2 = parseFloat(record.OeV_Fahrrad_Bewegung) || 0;
            break;
          case "Pkw vs. Park&Ride":
            sport1 = parseFloat(record.PKW_Bewegung) || 0;
            sport2 = parseFloat(record.Auto_OeV_Bewegung) || 0;
            break;
          case "Pkw vs. Pedelec":
            sport1 = parseFloat(record.PKW_Bewegung) || 0;
            sport2 = parseFloat(record.Pedelec_Bewegung) || 0;
            break;
          case "Pkw vs. Fahrrad":
            sport1 = parseFloat(record.PKW_Bewegung) || 0;
            sport2 = parseFloat(record.Fahrrad_Bewegung) || 0;
            break;
          case "ÖV vs. Pkw":
            sport1 = parseFloat(record.OeV_Bewegung) || 0;
            sport2 = parseFloat(record.PKW_Bewegung) || 0;
            break;
          case "ÖV vs. Bike&Ride":
            sport1 = parseFloat(record.OeV_Bewegung) || 0;
            sport2 = parseFloat(record.OeV_Fahrrad_Bewegung) || 0;
            break;
          case "ÖV vs. Park&Ride":
            sport1 = parseFloat(record.OeV_Bewegung) || 0;
            sport2 = parseFloat(record.Auto_OeV_Bewegung) || 0;
            break;
          case "ÖV vs. Pedelec":
            sport1 = parseFloat(record.OeV_Bewegung) || 0;
            sport2 = parseFloat(record.Pedelec_Bewegung) || 0;
            break;
          case "ÖV vs. Fahrrad":
            sport1 = parseFloat(record.OeV_Bewegung) || 0;
            sport2 = parseFloat(record.Fahrrad_Bewegung) || 0;
            break;
          case "Bike&Ride vs. Pkw":
            sport1 = parseFloat(record.OeV_Fahrrad_Bewegung) || 0;
            sport2 = parseFloat(record.PKW_Bewegung) || 0;
            break;
          case "Bike&Ride vs. ÖV":
            sport1 = parseFloat(record.OeV_Fahrrad_Bewegung) || 0;
            sport2 = parseFloat(record.OeV_Bewegung) || 0;
            break;
          case "Bike&Ride vs. Park&Ride":
            sport1 = parseFloat(record.OeV_Fahrrad_Bewegung) || 0;
            sport2 = parseFloat(record.Auto_OeV_Bewegung) || 0;
            break;
          case "Bike&Ride vs. Pedelec":
            sport1 = parseFloat(record.OeV_Fahrrad_Bewegung) || 0;
            sport2 = parseFloat(record.Pedelec_Bewegung) || 0;
            break;
          case "Bike&Ride vs. Fahrrad":
            sport1 = parseFloat(record.OeV_Fahrrad_Bewegung) || 0;
            sport2 = parseFloat(record.Fahrrad_Bewegung) || 0;
            break;
          case "Park&Ride vs. Pkw":
            sport1 = parseFloat(record.Auto_OeV_Bewegung) || 0;
            sport2 = parseFloat(record.PKW_Bewegung) || 0;
            break;
          case "Pedelec vs. Pkw":
            sport1 = parseFloat(record.Pedelec_Bewegung) || 0;
            sport2 = parseFloat(record.PKW_Bewegung) || 0;
            break;
          case "Fahrrad vs. Pkw":
            sport1 = parseFloat(record.Fahrrad_Bewegung) || 0;
            sport2 = parseFloat(record.PKW_Bewegung) || 0;
            break;
        }

        if (sport1 >= 0 && sport2 >= 0) {
          // Find row index based on sport1 (comparison mode1 sport)
          let rowIndex = 11 - this.getSportIndex(sport1);

          // Find column index based on sport2 (comparison mode2 sport)
          let colIndex = 11 - this.getSportIndex(sport2);

          // Increment counter if valid indices found
          if (rowIndex >= 0 && colIndex >= 0) {
            matrix[rowIndex].values[colIndex]++;
          }
        }
      });

      // Convert zero values to null for display
      return matrix.map((row) => ({
        mode: row.mode,
        values: row.values.map((v) => (v === 0 ? null : v)),
      }));
    },
    neutralCount(): number {
      if (!this.currentMatrix?.length) return 0;

      let count = 0;
      this.currentMatrix.forEach((row, rowIndex) => {
        row.values.forEach((value, colIndex) => {
          if (value && this.isNeutralField(rowIndex, colIndex)) {
            count += value;
          }
        });
      });
      return count;
    },
    improvementCount(): number {
      if (!this.currentMatrix?.length) return 0;

      let count = 0;
      this.currentMatrix.forEach((row, rowIndex) => {
        row.values.forEach((value, colIndex) => {
          if (
            value &&
            (this.isGreenLightField(rowIndex, colIndex) ||
              this.isGreenDarkField(rowIndex, colIndex))
          ) {
            count += value;
          }
        });
      });
      return count;
    },
    worseningCount(): number {
      if (!this.currentMatrix?.length) return 0;

      let count = 0;
      this.currentMatrix.forEach((row, rowIndex) => {
        row.values.forEach((value, colIndex) => {
          if (
            value &&
            (this.isRedLightField(rowIndex, colIndex) ||
              this.isRedDarkField(rowIndex, colIndex))
          ) {
            count += value;
          }
        });
      });
      return count;
    },
    getColumnRanges() {
      // Get the ranges based on the selected metric
      switch (this.selectedMetric) {
        case "Entfernung":
          return [
            "0-5",
            "5-10",
            "10-15",
            "15-20",
            "20-30",
            "30-40",
            "40-50",
            "50-60",
            "60-80",
            "80-100",
            "100-120",
            ">120",
          ];
        case "Zeit":
          return [
            "0-5",
            "5-10",
            "10-15",
            "15-20",
            "20-30",
            "30-40",
            "40-50",
            "50-60",
            "60-80",
            "80-100",
            "100-120",
            ">120",
          ];
        case "CO₂":
          return [
            "0-0.5",
            "0.5-1",
            "1-2",
            "2-3",
            "3-4",
            "4-6",
            "6-8",
            "8-10",
            "10-12",
            "12-14",
            "14-15",
            ">15",
          ];
        case "Kosten":
          return [
            "0-2",
            "2-4",
            "4-6",
            "6-8",
            "8-10",
            "10-12",
            "12-14",
            "14-16",
            "16-18",
            "18-20",
            "20-25",
            ">25",
          ];
        case "Bewegung":
          return [
            ">600",
            "500-600",
            "450-500",
            "400-450",
            "350-400",
            "300-350",
            "250-300",
            "200-250",
            "150-200",
            "100-150",
            "50-100",
            "0-50",
          ];
        default:
          return [
            "0-5",
            "5-10",
            "10-15",
            "15-20",
            "20-30",
            "30-40",
            "40-50",
            "50-60",
            "60-80",
            "80-100",
            "100-120",
            ">120",
          ];
      }
    },
    optimalTransportMatrix() {
      if (!this.crImportResults?.length) return [];

      const matrix = this.transportModes.map((mode) => ({
        mode: this.transportModeDisplayNames[mode] || mode,
        dbMode: mode,
        counts: Array(12).fill(0),
      }));

      // Map for special distance column cases
      const distanceColumnMap = {
        Bike_and_Ride: "OeV_Fahrrad",
        Park_and_Ride: "Auto_OeV",
      };

      this.crImportResults.forEach((record) => {
        const metricColumn =
          this.selectedOptimalMetric === "Entfernung"
            ? "Nutzen_effektivste_VM"
            : this.selectedOptimalMetric === "Zeit"
              ? "Zeit_effektivstes_VM"
              : this.selectedOptimalMetric === "CO₂"
                ? "CO2_effektivstes_VM"
                : this.selectedOptimalMetric === "Kosten"
                  ? "Kosten_effektivstes_VM"
                  : this.selectedOptimalMetric === "Bewegung"
                    ? "Bewegungs_effektivstes_VM"
                    : "Nutzen_effektivste_VM";

        const optimalMode = record[metricColumn];

        if (optimalMode && this.transportModes.includes(optimalMode)) {
          // Get the correct prefix for the distance column
          const distancePrefix = distanceColumnMap[optimalMode] || optimalMode;
          const distanceColumn = `${distancePrefix}_Entfernung`;
          const distance = parseFloat(record[distanceColumn]) || 0;

          if (distance > 0) {
            const colIndex = this.getDistanceColumnIndex(distance);
            const rowIndex = this.transportModes.indexOf(optimalMode);
            matrix[rowIndex].counts[colIndex]++;
          }
        }
      });

      return matrix;
    },

    // Generate list of alternative transport columns (excluding current transport)
    alternativeTransportColumns() {
      const allTransports = [
        { key: "Fahrrad", display: "Fahrrad", selector: "Fahrrad" },
        { key: "Pedelec", display: "Pedelec", selector: "Pedelec" },
        { key: "ÖV", display: "ÖPNV", selector: "ÖV" },
        { key: "Bike & Ride", display: "Bike&Ride", selector: "Bike & Ride" },
        { key: "Park & Ride", display: "Park&Ride", selector: "Park & Ride" },
        { key: "Zu Fuß", display: "Zu Fuß", selector: "Zu Fuß" },
        // { key: "E-Auto", display: "E-Auto", selector: "E-Auto" },
        { key: "Auto", display: "Pkw", selector: "Auto" },
      ];

      // Check if any employee has Werksbus distance data
      const hasWerksbusData = this.crImportResults.some((record) => {
        const werksbusDistance = record["Werksbus_Entfernung"];
        return werksbusDistance !== null && werksbusDistance !== undefined;
      });

      // Add Werksbus and Bike & Werksbus if data exists
      if (hasWerksbusData) {
        allTransports.push(
          { key: "Werksbus", display: "Werksbus", selector: "Werksbus" },
          {
            key: "Bike & Werksbus",
            display: "Bike&Werksbus",
            selector: "Bike & Werksbus",
          },
        );
      }

      // Filter out the current transport
      return allTransports.filter(
        (transport) =>
          transport.selector !== this.selectedBestAlternativeCurrentTransport,
      );
    },

    // Transport columns considered for this project's best-alternative analysis
    consideredAlternativeColumns() {
      const allColumns = [
        {
          key: "Fahrrad",
          display: "Fahrrad",
          selector: "Fahrrad",
          dataFields: ["Fahrrad_Entfernung", "Fahrrad_Zeit"],
        },
        {
          key: "Pedelec",
          display: "Pedelec",
          selector: "Pedelec",
          dataFields: ["Pedelec_Entfernung", "Pedelec_Zeit"],
        },
        {
          key: "ÖV",
          display: "ÖPNV",
          selector: "ÖV",
          dataFields: ["OeV_Entfernung", "OeV_Zeit"],
        },
        {
          key: "Bike & Ride",
          display: "B&R",
          selector: "Bike & Ride",
          dataFields: ["OeV_Fahrrad_Entfernung", "OeV_Fahrrad_Zeit"],
        },
        {
          key: "Park & Ride",
          display: "P&R",
          selector: "Park & Ride",
          dataFields: ["Auto_OeV_Entfernung", "Auto_OeV_Zeit"],
        },
        {
          key: "Zu Fuß",
          display: "zu Fuß",
          selector: "Zu Fuß",
          dataFields: ["Fuss_Weg_Entfernung", "Fuss_Weg_Zeit"],
        },
        {
          key: "Werksbus",
          display: "Werksbus",
          selector: "Werksbus",
          dataFields: ["Werksbus_Entfernung", "Werksbus_Zeit"],
        },
        {
          key: "Bike & Werksbus",
          display: "Bike&Werksbus",
          selector: "Bike & Werksbus",
          dataFields: ["Fahrrad_Werksbus_Entfernung", "Fahrrad_Werksbus_Zeit"],
        },
        {
          key: "Bahnreaktivierung & Ride",
          display: "Bahnreakt.",
          selector: "Bahnreaktivierung & Ride",
          dataFields: ["Bahnreakt_Ride_Entfernung", "Bahnreakt_Ride_Zeit"],
        },
        {
          key: "Park & Ride & Bike",
          display: "P&R&B",
          selector: "Park & Ride & Bike",
          dataFields: [
            "Park_Ride_and_Bike_Entfernung",
            "Park_Ride_and_Bike_Zeit",
          ],
        },
      ];

      const records = Array.isArray(this.crImportResults)
        ? this.crImportResults
        : [];
      const hasAnyPositiveValue = (
        record: Record<string, any>,
        field: string,
      ) => {
        const value = Number(record[field]);
        return Number.isFinite(value) && value > 0;
      };

      return allColumns.filter((transport) =>
        records.some((record) =>
          transport.dataFields.some((field) =>
            hasAnyPositiveValue(record, field),
          ),
        ),
      );
    },

    // Calculate total summary across all distance ranges
    totalSummary() {
      if (
        !this.templateStyleAnalysisResults ||
        this.templateStyleAnalysisResults.length === 0
      ) {
        return {
          total_employees: 0,
          employees_without_alternative: 0,
          total_with_alternatives: 0,
          success_percentage: 0,
          no_alternative_percentage: 0,
          transport_totals: {},
        };
      }

      const totals = this.templateStyleAnalysisResults.reduce(
        (acc: any, row: any) => {
          acc.total_employees += row.total_employees || 0;
          acc.employees_without_alternative +=
            row.employees_without_alternative || 0;
          acc.total_with_alternatives += row.total_with_alternatives || 0;

          // Sum up transport counts
          for (const transport of this.consideredAlternativeColumns) {
            const count = row.transport_counts[transport.selector] || 0;
            acc.transport_totals[transport.selector] =
              (acc.transport_totals[transport.selector] || 0) + count;
          }

          return acc;
        },
        {
          total_employees: 0,
          employees_without_alternative: 0,
          total_with_alternatives: 0,
          transport_totals: {} as Record<string, number>,
        },
      );

      // Calculate percentages
      const successPercentage =
        totals.total_employees > 0
          ? Math.round(
              (totals.total_with_alternatives / totals.total_employees) * 100,
            )
          : 0;
      const noAlternativePercentage =
        totals.total_employees > 0
          ? Math.round(
              (totals.employees_without_alternative / totals.total_employees) *
                100,
            )
          : 0;

      return {
        ...totals,
        success_percentage: successPercentage,
        no_alternative_percentage: noAlternativePercentage,
      };
    },

    // Calculate dynamic column width - simple equal distribution
    alternativeColumnWidth() {
      const numTransportColumns = this.alternativeTransportColumns.length + 1; // +1 for Sum column
      // Simple equal width for all transport columns
      const width = 100 / numTransportColumns;
      return Math.round(width);
    },

    // Generate criteria summary abbreviations for display
    criteriaSummaryText() {
      const constraints = this.bestAlternativeConstraints;
      const metric = this.selectedBestAlternativeMetric;
      const metricText = this.bestAlternativeMetricModes.find(
        (mode) => mode.value === metric,
      )?.text;
      console.log("metric in criteriaSummaryText: ", metricText);
      const parts = [];

      // Time percentage constraint
      if (
        constraints.max_time_percentage !== null &&
        constraints.max_time_percentage !== undefined
      ) {
        parts.push(`${metricText}: ≤ ${constraints.max_time_percentage} %`);
      }

      // Additional minutes constraint
      if (
        constraints.max_additional_minutes !== null &&
        constraints.max_additional_minutes !== undefined
      ) {
        parts.push(`+ ${constraints.max_additional_minutes} min`);
      }

      // Metric improvement constraint (only if enforced)
      if (
        constraints.enforce_metric_constraints &&
        constraints.metric_improvement_percentage !== null &&
        constraints.metric_improvement_percentage !== undefined
      ) {
        const sign = constraints.metric_improvement_percentage >= 0 ? "+" : "";
        parts.push(
          `Opt: ${sign} ${constraints.metric_improvement_percentage} %`,
        );
      }

      // Max legs constraint
      if (constraints.max_legs !== null && constraints.max_legs !== undefined) {
        parts.push(`≤ ${constraints.max_legs} Umst.`);
      }

      return parts.length > 0
        ? `Filterkriterien: ${parts.join(", ")}`
        : "Keine Einschränkungen";
    },
    analysisBaselineTransportLabel() {
      const headerLabelMap: Record<string, string> = {
        Auto: "Pkw",
      };
      return (
        headerLabelMap[this.selectedBestAlternativeCurrentTransport] ||
        this.selectedBestAlternativeCurrentTransport
      );
    },
    comparisonMetricConfig() {
      const configs: Record<
        string,
        {
          title: string;
          unitLabel: string;
          precision: number;
          adaptivePrecision: boolean;
          higherIsBetter: boolean;
        }
      > = {
        co2: {
          title: "Emissionen",
          unitLabel: "CO₂ in kg",
          precision: 1,
          adaptivePrecision: true,
          higherIsBetter: false,
        },
        time: {
          title: "Zeit",
          unitLabel: "Min.",
          precision: 1,
          adaptivePrecision: false,
          higherIsBetter: false,
        },
        cost: {
          title: "Kosten",
          unitLabel: "Euro",
          precision: 2,
          adaptivePrecision: false,
          higherIsBetter: false,
        },
        sport: {
          title: "Sport",
          unitLabel: "Schritte",
          precision: 0,
          adaptivePrecision: false,
          higherIsBetter: true,
        },
        distance: {
          title: "Distanz",
          unitLabel: "km",
          precision: 2,
          adaptivePrecision: false,
          higherIsBetter: false,
        },
      };
      return configs[this.selectedComparisonMetric] || configs.co2;
    },
    comparisonMetricGroupHeader() {
      return `${this.comparisonMetricConfig.title} (${this.comparisonMetricConfig.unitLabel})`;
    },
    emissionsBaselineSubLabel() {
      return this.useSurveyTransportBaselineForEmissions
        ? "Zurzeit genutztes VM"
        : this.analysisBaselineTransportLabel;
    },
    emissionsDifferenceGroupLabel() {
      return this.useSurveyTransportBaselineForEmissions
        ? "Differenz genutztes VM"
        : `Differenz zu ${this.analysisBaselineTransportLabel}`;
    },
    emissionComparisonRows() {
      const ranges = this.getBestAlternativeDistanceRanges();
      const selectedMetric = this.selectedComparisonMetric;
      const rowsByRange = Object.fromEntries(
        ranges.map((range) => [
          range.label,
          {
            distance_range: range.label,
            total_employees: 0,
            total_with_alternatives: 0,
            employees_without_alternative: 0,
            no_alternative_percentage: 0,
            baseline_value: 0,
            best_alternative_value: 0,
            no_best_alternative_value: 0,
            combined_value: 0,
            difference_absolute: 0,
            difference_relative_percent: 0,
          },
        ]),
      ) as Record<string, any>;

      if (Array.isArray(this.templateStyleAnalysisResults)) {
        this.templateStyleAnalysisResults.forEach((row: any) => {
          const target = rowsByRange[row.distance_range];
          if (!target) return;
          target.total_employees = row.total_employees || 0;
          target.total_with_alternatives = row.total_with_alternatives || 0;
          target.employees_without_alternative =
            row.employees_without_alternative || 0;
          target.no_alternative_percentage =
            this.getNoAlternativePercentage(target);
        });
      }

      const employeeMap = new Map<string, any>();
      if (Array.isArray(this.crImportResults)) {
        this.crImportResults.forEach((record: any, index: number) => {
          employeeMap.set(this.resolveEmployeeId(record, index), record);
        });
      }

      const resultRows = Array.isArray(this.bestAlternativePointResults)
        ? this.bestAlternativePointResults
        : [];

      resultRows.forEach((result: any) => {
        const employeeData = employeeMap.get(String(result.address_id));
        if (!employeeData) return;

        const pkwDistance = Number(
          this.getDistanceForTransport(employeeData, "Auto"),
        );
        const rangeLabel = this.getDistanceRangeLabel(pkwDistance);
        if (!rangeLabel) return;

        const row = rowsByRange[rangeLabel];
        if (!row) return;

        const baselineTransport = this.useSurveyTransportBaselineForEmissions
          ? result.current_transport ||
            this.selectedBestAlternativeCurrentTransport
          : this.selectedBestAlternativeCurrentTransport;
        const baselineValue = this.getTransportMetricValue(
          employeeData,
          baselineTransport,
          selectedMetric,
        );
        row.baseline_value += baselineValue;

        if (result.meets_constraints) {
          const recommendedValue = this.getTransportMetricValue(
            employeeData,
            result.recommended_transport,
            selectedMetric,
          );
          row.best_alternative_value += recommendedValue;
        } else {
          row.no_best_alternative_value += baselineValue;
        }
      });

      return ranges.map((range) => {
        const row = rowsByRange[range.label];
        row.combined_value =
          row.best_alternative_value + row.no_best_alternative_value;
        row.difference_absolute = row.combined_value - row.baseline_value;
        row.difference_relative_percent =
          row.baseline_value > 0
            ? (row.difference_absolute / row.baseline_value) * 100
            : 0;
        return row;
      });
    },
    emissionComparisonTotals() {
      const totals = this.emissionComparisonRows.reduce(
        (acc: any, row: any) => {
          acc.total_employees += row.total_employees || 0;
          acc.total_with_alternatives += row.total_with_alternatives || 0;
          acc.employees_without_alternative +=
            row.employees_without_alternative || 0;
          acc.baseline_value += row.baseline_value || 0;
          acc.best_alternative_value += row.best_alternative_value || 0;
          acc.no_best_alternative_value += row.no_best_alternative_value || 0;
          return acc;
        },
        {
          total_employees: 0,
          total_with_alternatives: 0,
          employees_without_alternative: 0,
          no_alternative_percentage: 0,
          baseline_value: 0,
          best_alternative_value: 0,
          no_best_alternative_value: 0,
          combined_value: 0,
          difference_absolute: 0,
          difference_relative_percent: 0,
        },
      );

      totals.no_alternative_percentage =
        totals.total_employees > 0
          ? Math.round(
              (totals.employees_without_alternative / totals.total_employees) *
                100,
            )
          : 0;
      totals.combined_value =
        totals.best_alternative_value + totals.no_best_alternative_value;
      totals.difference_absolute =
        totals.combined_value - totals.baseline_value;
      totals.difference_relative_percent =
        totals.baseline_value > 0
          ? (totals.difference_absolute / totals.baseline_value) * 100
          : 0;
      return totals;
    },
  },

  watch: {
    selectedComparison() {
      // Recalculate matrix when comparison mode changes
      console.log("selectedComparison", this.selectedComparison);
      this.currentMatrix;
    },
    selectedMetric(newVal) {
      console.log("Metric changed to:", newVal);
    },
    // Watch Best Alternative settings and recalculate with OPTIMIZED unified analysis
    selectedBestAlternativeCurrentTransport() {
      if (this.bestAlternativeCalculated) {
        this.updateAllBestAlternativeData();
      }
    },
    selectedBestAlternativeMetric() {
      if (this.bestAlternativeCalculated) {
        this.updateAllBestAlternativeData();
      }
    },
    bestAlternativeConstraints: {
      handler() {
        if (this.bestAlternativeCalculated) {
          this.updateAllBestAlternativeData();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.cleverRouteImportStore.fetchParameters();

    this.fetchResults();
  },

  methods: {
    getBestAlternativeDistanceRanges() {
      return [
        { label: "0 - 5", min: 0, max: 5, includeMin: true },
        { label: "5 - 10", min: 5, max: 10, includeMin: false },
        { label: "10 - 20", min: 10, max: 20, includeMin: false },
        { label: "20 - 30", min: 20, max: 30, includeMin: false },
        { label: "30 - 50", min: 30, max: 50, includeMin: false },
        { label: "50+", min: 50, max: Infinity, includeMin: false },
      ];
    },
    getDistanceRangeLabel(distance: number): string | null {
      if (!Number.isFinite(distance) || distance <= 0) {
        return null;
      }
      const range = this.getBestAlternativeDistanceRanges().find((entry) => {
        const withinLowerBound = entry.includeMin
          ? distance >= entry.min
          : distance > entry.min;
        return withinLowerBound && distance <= entry.max;
      });
      return range?.label || null;
    },
    resolveEmployeeId(employee: any, index: number): string {
      const employeeIdRaw =
        employee.MA_ID ??
        employee.ma_id ??
        employee.origin_id ??
        employee.id ??
        index;
      return employeeIdRaw !== null &&
        employeeIdRaw !== undefined &&
        employeeIdRaw !== ""
        ? String(employeeIdRaw)
        : String(index);
    },
    getTransportMetricValue(
      record: any,
      transport: string,
      metric: string,
    ): number {
      if (metric === "distance") {
        const distance = Number(
          this.getDistanceForTransport(record, transport),
        );
        return Number.isFinite(distance) && distance > 0 ? distance : 0;
      }

      let value = Number(
        this.getMetricValueForTransport(record, transport, metric),
      );

      // Keep existing fallback behavior for car CO2 values.
      if (
        metric === "co2" &&
        (transport === "Auto" || transport === "PKW") &&
        !value
      ) {
        value = Number(record.E_Auto_CO2) || 0;
      }

      if (!Number.isFinite(value) || value <= 0) {
        return 0;
      }

      // Convert grams to kilograms for CO2 table display.
      if (metric === "co2") {
        return value / 1000;
      }

      return value;
    },
    // UNIFIED ANALYSIS ENGINE - Single source of truth for all best alternative calculations
    performBestAlternativeAnalysis: async function () {
      // console.log("🚀 Starting unified best alternative analysis...");

      if (!this.crImportResults?.length || !this.bestAlternativeCalculated) {
        // console.log(
        //   "❌ Analysis conditions not met - no data or not calculated",
        // );
        return { analysisData: [], templateData: [], success: false };
      }
      this.isCalculatingAnalysisData = true;
      const legCountCache = new Map(); // Cache for leg count API calls

      try {
        const currentTransport = this.selectedBestAlternativeCurrentTransport;
        const metric = this.selectedBestAlternativeMetric;
        const constraints = this.bestAlternativeConstraints;

        // console.log(
        //   `📊 Analysis settings: Transport=${currentTransport}, Metric=${metric}, MaxLegs=${constraints.max_legs}`,
        // );

        // Define distance ranges (single source of truth)
        const distanceRanges = this.getBestAlternativeDistanceRanges();

        const analysisResults = [];
        const templateResults = [];

        // Grand totals across ALL ranges for office-wide comparison
        let grandTotalPkwTime = 0;
        let grandTotalPkwDistance = 0;
        let grandTotalPkwCO2 = 0;
        // ═══════════════════════════════════════════════════════════════
        // OFFICE-WIDE COMPARISON: Calculate best alternatives for ALL employees
        // ═══════════════════════════════════════════════════════════════
        const mapOutputStore = useMapOutputStore();

        // Transform crImportResults to routeData format (object keyed by ID)
        const routeData = {};
        const addresses = [];
        this.crImportResults.forEach((employee, index) => {
          const employeeIdRaw =
            employee.MA_ID ??
            employee.ma_id ??
            employee.origin_id ??
            employee.id ??
            index;
          const employeeId =
            employeeIdRaw !== null &&
            employeeIdRaw !== undefined &&
            employeeIdRaw !== ""
              ? String(employeeIdRaw)
              : String(index);
          const latitude = Number(
            employee.latitude ?? employee.Latitude ?? employee.lat,
          );
          const longitude = Number(
            employee.longitude ?? employee.Longitude ?? employee.lng,
          );

          routeData[employeeId] = employee; // Employee data already has all the fields needed
          addresses.push({
            id: employeeId,
            lat: Number.isFinite(latitude) ? latitude : 0,
            lng: Number.isFinite(longitude) ? longitude : 0,
          });
        });

        // Convert filters to BestAlternativeFilters format
        const filters = {
          current_transport: currentTransport,
          selected_metric: metric,
          max_time_percentage: constraints.max_time_percentage,
          max_additional_minutes: constraints.max_additional_minutes,
          max_legs: constraints.max_legs,
          enforce_metric_constraints:
            constraints.enforce_metric_constraints || false,
          metric_improvement_percentage:
            constraints.metric_improvement_percentage || 0,
        };

        // Fetch transport parameters (required by calculateBestAlternatives)
        const transportParams =
          await mapOutputStore.fetchAllTransportParameters();

        // Call mapOutputStore function to calculate best alternatives for ALL employees

        // console.log(`🔄 Calling calculateBestAlternatives() for ${addresses.length} employees...`);
        const bestAlternativeResults =
          await mapOutputStore.calculateBestAlternatives(
            routeData as any, // Type cast: function signature says array but uses it as object
            addresses,
            filters,
            transportParams,
          );
        this.bestAlternativePointResults = bestAlternativeResults;

        // Calculate totals from results
        let grandTotalChosenTime = 0;
        let grandTotalChosenDistance = 0;
        let grandTotalChosenCO2 = 0;

        bestAlternativeResults.forEach((result) => {
          const employeeData = routeData[result.address_id];
          if (!employeeData) return;

          // Get time and distance for the recommended transport
          const chosenTime = mapOutputStore.getMetricValueForTransport(
            employeeData,
            result.recommended_transport,
            "time",
          );
          const chosenDistance = mapOutputStore.getDistanceForTransport(
            employeeData,
            result.recommended_transport,
          );
          let chosenCO2Grams = mapOutputStore.getMetricValueForTransport(
            employeeData,
            result.recommended_transport,
            "co2",
          );
          if (result.recommended_transport === "PKW" && !chosenCO2Grams) {
            chosenCO2Grams = parseFloat(employeeData.E_Auto_CO2) || 0;
          }
          const chosenCO2Kg = chosenCO2Grams ? chosenCO2Grams / 1000 : 0;

          grandTotalChosenTime += chosenTime || 0;
          grandTotalChosenDistance += chosenDistance || 0;
          grandTotalChosenCO2 += chosenCO2Kg;
        });

        // console.log(
        //   `✅ Best alternatives calculated: ${grandTotalChosenTime.toFixed(2)} min, ${grandTotalChosenDistance.toFixed(2)} km`,
        // );

        // ═══════════════════════════════════════════════════════════════
        // FILTER: Only include employees with valid PKW or E_Auto distance data
        // This ensures all three calculations use the SAME employee set
        // ═══════════════════════════════════════════════════════════════
        const validEmployeeResults = bestAlternativeResults.filter((result) => {
          const employee = routeData[result.address_id];
          if (!employee) return false;

          const pkwDistance = parseFloat(employee.PKW_Entfernung) || 0;
          const eAutoDistance = parseFloat(employee.E_Auto_Entfernung) || 0;

          // Employee is valid if they have either PKW or E_Auto distance
          return pkwDistance > 0 || eAutoDistance > 0;
        });

        console.log(
          `📋 Employee filtering: ${bestAlternativeResults.length} total → ${validEmployeeResults.length} with valid car data (${bestAlternativeResults.length - validEmployeeResults.length} excluded)`,
        );

        // ═══════════════════════════════════════════════════════════════
        // PKW BASELINE: Calculate PKW totals for the filtered employee set
        // ═══════════════════════════════════════════════════════════════
        validEmployeeResults.forEach((result) => {
          const employee = routeData[result.address_id];

          let pkwDistance = parseFloat(employee.PKW_Entfernung) || 0;
          let pkwTime = parseFloat(employee.PKW_Zeit) || 0;
          let pkwCO2Grams = parseFloat(employee.PKW_CO2) || 0;

          // If PKW has no valid distance, use E_Auto (guaranteed to exist due to filtering)
          if (pkwDistance === 0) {
            pkwDistance = parseFloat(employee.E_Auto_Entfernung) || 0;
            pkwTime = parseFloat(employee.E_Auto_Zeit) || 0;
            pkwCO2Grams = parseFloat(employee.E_Auto_CO2) || 0;
          }

          grandTotalPkwTime += pkwTime;
          grandTotalPkwDistance += pkwDistance;
          grandTotalPkwCO2 += pkwCO2Grams / 1000;
        });

        // ═══════════════════════════════════════════════════════════════
        // FASTEST TRANSPORT: Calculate totals if each employee uses their fastest transport
        // Uses the same filtered employee set for fair comparison
        // ═══════════════════════════════════════════════════════════════
        let grandTotalFastestTime = 0;
        let grandTotalFastestDistance = 0;
        let grandTotalFastestCO2 = 0;
        const fastestTransportCounts: Record<string, number> = {}; // Track which transports are selected

        // DEBUG: Log available _Zeit columns from first employee
        const firstEmployee =
          validEmployeeResults.length > 0
            ? routeData[validEmployeeResults[0].address_id]
            : null;
        if (firstEmployee) {
          const allZeitColumns = Object.keys(firstEmployee).filter((key) =>
            key.endsWith("_Zeit"),
          );
          console.log("\n🔍 DEBUG: Available _Zeit columns:", allZeitColumns);
        }

        validEmployeeResults.forEach((result) => {
          const employee = routeData[result.address_id];

          // Find all time columns (properties ending with '_Zeit')
          const timeColumns = Object.keys(employee).filter((key) =>
            key.endsWith("_Zeit"),
          );

          // Find the fastest (minimum) time among all transports
          let fastestTime = Infinity;
          let fastestTransportName = null;

          timeColumns.forEach((timeCol) => {
            const timeValue = parseFloat(employee[timeCol]);
            if (timeValue && timeValue > 0 && timeValue < fastestTime) {
              // Extract transport name by removing '_Zeit' suffix
              const transportName = timeCol.replace("_Zeit", "");

              // CRITICAL: Only consider this transport if it has a valid distance entry
              // Transports exceeding max_range won't have distance data
              const distanceCol = `${transportName}_Entfernung`;
              const distanceValue = parseFloat(employee[distanceCol]) || 0;

              if (distanceValue > 0) {
                fastestTime = timeValue;
                fastestTransportName = transportName;
              }
            }
          });

          // If we found a valid fastest transport, get its distance and co2
          if (fastestTransportName && fastestTime !== Infinity) {
            const distanceCol = `${fastestTransportName}_Entfernung`;
            const fastestDistance = parseFloat(employee[distanceCol]) || 0;
            let fastestCO2Grams =
              parseFloat(employee[`${fastestTransportName}_CO2`]) || 0;
            if (fastestTransportName === "PKW" && !fastestCO2Grams) {
              fastestCO2Grams = parseFloat(employee.E_Auto_CO2) || 0;
            }

            // Track which transport was selected
            fastestTransportCounts[fastestTransportName] =
              (fastestTransportCounts[fastestTransportName] || 0) + 1;

            grandTotalFastestTime += fastestTime;
            grandTotalFastestDistance += fastestDistance;
            grandTotalFastestCO2 += fastestCO2Grams / 1000;
          } else {
            // Fallback: If no valid fastest transport found, use PKW (or E_Auto as fallback)
            let fallbackDistance = parseFloat(employee.PKW_Entfernung) || 0;
            let fallbackTime = parseFloat(employee.PKW_Zeit) || 0;
            let fallbackCO2Grams = parseFloat(employee.PKW_CO2) || 0;

            // If PKW has no distance, use E_Auto (guaranteed to exist due to filtering)
            if (fallbackDistance === 0) {
              fallbackDistance = parseFloat(employee.E_Auto_Entfernung) || 0;
              fallbackTime = parseFloat(employee.E_Auto_Zeit) || 0;
              fallbackCO2Grams = parseFloat(employee.E_Auto_CO2) || 0;
            }

            fastestTransportCounts["PKW_FALLBACK"] =
              (fastestTransportCounts["PKW_FALLBACK"] || 0) + 1;

            grandTotalFastestTime += fallbackTime;
            grandTotalFastestDistance += fallbackDistance;
            grandTotalFastestCO2 += fallbackCO2Grams / 1000;
          }
        });

        // Log transport selection counts
        console.log("\n🔍 Fastest Transport Selection Breakdown:");
        Object.entries(fastestTransportCounts)
          .sort((a, b) => (b[1] as number) - (a[1] as number))
          .forEach(([transport, count]) => {
            console.log(`  ${transport}: ${count} employees`);
          });

        // DEBUG: Log CO2 per transport type
        console.log("\n🔬 DEBUG: CO2 Contribution by Fastest Transport:");
        const co2ByTransport: Record<string, { total: number; count: number }> =
          {};
        validEmployeeResults.forEach((result) => {
          const employee = routeData[result.address_id];
          if (!employee) return;

          const timeColumns = Object.keys(employee).filter((key) =>
            key.endsWith("_Zeit"),
          );
          let fastestTime = Infinity;
          let fastestTransportName: string | null = null;

          timeColumns.forEach((timeCol) => {
            const timeValue = parseFloat(employee[timeCol]);
            if (timeValue && timeValue > 0 && timeValue < fastestTime) {
              const transportName = timeCol.replace("_Zeit", "");
              // Only consider if valid distance exists
              const distanceValue =
                parseFloat(employee[`${transportName}_Entfernung`]) || 0;
              if (distanceValue > 0) {
                fastestTime = timeValue;
                fastestTransportName = transportName;
              }
            }
          });

          if (fastestTransportName && fastestTime !== Infinity) {
            let co2Grams =
              parseFloat(employee[`${fastestTransportName}_CO2`]) || 0;
            if (fastestTransportName === "PKW" && !co2Grams) {
              co2Grams = parseFloat(employee.E_Auto_CO2) || 0;
            }
            const co2Kg = co2Grams / 1000;

            if (!co2ByTransport[fastestTransportName]) {
              co2ByTransport[fastestTransportName] = { total: 0, count: 0 };
            }
            co2ByTransport[fastestTransportName].total += co2Kg;
            co2ByTransport[fastestTransportName].count += 1;

            // Log if CO2 is unusually high (> 5kg per trip)
            if (co2Kg > 5) {
              console.log(
                `  ⚠️ High CO2: ${fastestTransportName} = ${co2Kg.toFixed(2)} kg for employee ${result.address_id}`,
              );
            }
          }
        });

        console.log("\n  📊 CO2 Totals by Transport (sorted by total):");
        Object.entries(co2ByTransport)
          .sort((a, b) => b[1].total - a[1].total)
          .forEach(([transport, data]) => {
            const avg = data.count > 0 ? data.total / data.count : 0;
            console.log(
              `    ${transport}: ${data.total.toFixed(2)} kg total (${data.count} employees, avg: ${avg.toFixed(3)} kg/employee)`,
            );
          });

        const validEmployeeCount = validEmployeeResults.length;
        const excludedCount =
          bestAlternativeResults.length - validEmployeeCount;

        console.log(
          `⚡ Fastest transports calculated: ${grandTotalFastestTime.toFixed(2)} min, ${grandTotalFastestDistance.toFixed(2)} km, ${grandTotalFastestCO2.toFixed(2)} kg CO2 across ${validEmployeeCount} employees`,
        );
        console.log(
          `📊 PKW Baseline calculated: ${grandTotalPkwTime.toFixed(2)} min, ${grandTotalPkwDistance.toFixed(2)} km, ${grandTotalPkwCO2.toFixed(2)} kg CO2 across ${validEmployeeCount} employees`,
        );
        console.log(
          `🔍 COMPARISON: PKW CO2=${grandTotalPkwCO2.toFixed(2)} kg, Fastest CO2=${grandTotalFastestCO2.toFixed(2)} kg, Difference=${(grandTotalFastestCO2 - grandTotalPkwCO2).toFixed(2)} kg`,
        );
        if (excludedCount > 0) {
          console.log(
            `⚠️ Note: ${excludedCount} employees excluded from all calculations (no valid PKW or E_Auto distance data)`,
          );
        }

        // ═══════════════════════════════════════════════════════════════
        // VALIDATION: Log per-result breakdown
        // ═══════════════════════════════════════════════════════════════
        // console.log(
        //   `\n🔍 VALIDATION: Per-Employee Breakdown (${bestAlternativeResults.length} employees)\n${"=".repeat(80)}`,
        // );

        // // Group results by recommended transport for summary
        // const transportGroups: Record<string, any[]> = {};
        // bestAlternativeResults.forEach((result) => {
        //   const transport = result.recommended_transport;
        //   if (!transportGroups[transport]) {
        //     transportGroups[transport] = [];
        //   }
        //   transportGroups[transport].push(result);
        // });

        // // Log summary by transport
        // Object.keys(transportGroups)
        //   .sort()
        //   .forEach((transport) => {
        //     const results = transportGroups[transport];
        //     const totalTime = results.reduce((sum, r) => {
        //       const employeeData = routeData[r.address_id];
        //       return (
        //         sum +
        //         (mapOutputStore.getMetricValueForTransport(
        //           employeeData,
        //           transport,
        //           "time",
        //         ) || 0)
        //       );
        //     }, 0);
        //     const totalDistance = results.reduce((sum, r) => {
        //       const employeeData = routeData[r.address_id];
        //       return (
        //         sum +
        //         (mapOutputStore.getDistanceForTransport(
        //           employeeData,
        //           transport,
        //         ) || 0)
        //       );
        //     }, 0);

        //     console.log(`\n📊 ${transport}: ${results.length} employees`);
        //     console.log(`   Total Time: ${totalTime.toFixed(2)} min`);
        //     console.log(`   Total Distance: ${totalDistance.toFixed(2)} km`);
        //     console.log(
        //       `   Avg Time: ${(totalTime / results.length).toFixed(2)} min/employee`,
        //     );
        //     console.log(
        //       `   Avg Distance: ${(totalDistance / results.length).toFixed(2)} km/employee`,
        //     );
        //   });

        // // Log count of employees who found alternatives vs. stayed with current
        // const foundAlternatives = bestAlternativeResults.filter(
        //   (r) => r.meets_constraints,
        // ).length;
        // const stayedWithCurrent = bestAlternativeResults.length - foundAlternatives;
        // console.log(`\n📈 Summary:`);
        // console.log(
        //   `   Found valid alternatives: ${foundAlternatives} (${((foundAlternatives / bestAlternativeResults.length) * 100).toFixed(1)}%)`,
        // );
        // console.log(
        //   `   Stayed with ${currentTransport}: ${stayedWithCurrent} (${((stayedWithCurrent / bestAlternativeResults.length) * 100).toFixed(1)}%)`,
        // );
        // console.log(`${"=".repeat(80)}\n`);

        // Calculate differences and percentages
        const totalEmployeesAnalyzed = this.crImportResults.length;
        const timeDifference = grandTotalPkwTime - grandTotalChosenTime;
        const distanceDifference =
          grandTotalPkwDistance - grandTotalChosenDistance;
        const co2Difference = grandTotalPkwCO2 - grandTotalChosenCO2;

        const co2ChangePercent =
          grandTotalPkwCO2 > 0
            ? ((co2Difference / grandTotalPkwCO2) * 100).toFixed(1)
            : "0.0";
        const timeChangePercent =
          grandTotalPkwTime > 0
            ? ((timeDifference / grandTotalPkwTime) * 100).toFixed(1)
            : "0.0";
        const distanceChangePercent =
          grandTotalPkwDistance > 0
            ? (distanceDifference / grandTotalPkwDistance) * 100 > 0
              ? ((distanceDifference / grandTotalPkwDistance) * 100).toFixed(1)
              : "0.0"
            : "0.0";

        // Calculate fastest transport differences and percentages
        const fastestTimeDiff = grandTotalPkwTime - grandTotalFastestTime;
        const fastestDistanceDiff =
          grandTotalPkwDistance - grandTotalFastestDistance;
        const fastestCO2Diff = grandTotalPkwCO2 - grandTotalFastestCO2;
        const fastestTimePercent =
          grandTotalPkwTime > 0
            ? ((fastestTimeDiff / grandTotalPkwTime) * 100).toFixed(1)
            : "0.0";
        const fastestDistancePercent =
          grandTotalPkwDistance > 0
            ? ((fastestDistanceDiff / grandTotalPkwDistance) * 100).toFixed(1)
            : "0.0";
        const fastestCO2Percent =
          grandTotalPkwCO2 > 0
            ? ((fastestCO2Diff / grandTotalPkwCO2) * 100).toFixed(1)
            : "0.0";

        // Output office-wide comparison with 3-way comparison
        console.log(`
╔═══════════════════════════════════════════════════════════╗
║         🏢 OFFICE-WIDE COMPARISON: PKW vs. ALTERNATIVES   ║
╚═══════════════════════════════════════════════════════════╝

📊 Current Transport: ${currentTransport}
📊 Optimization Metric: ${metric}
📊 Constraints: max_time=${constraints.max_time_percentage}%, max_additional=${constraints.max_additional_minutes}min, max_legs=${constraints.max_legs || "unlimited"}
👥 Total Employees Analyzed: ${totalEmployeesAnalyzed}

═══════════════════════════════════════════════════════════
⏱️  TOTAL TIME COMPARISON (minutes):
═══════════════════════════════════════════════════════════
   PKW Baseline (nur Auto):     ${grandTotalPkwTime.toFixed(2)} min
   With Best Alternatives:       ${grandTotalChosenTime.toFixed(2)} min
   ⚡ Fastest Transports:         ${grandTotalFastestTime.toFixed(2)} min
   ───────────────────────────────────────────────────────
   Best Alt. vs PKW:             ${timeDifference.toFixed(2)} min (${timeChangePercent}%)
   Fastest vs PKW:               ${fastestTimeDiff.toFixed(2)} min (${fastestTimePercent}%)
   Per-Employee Average (Best):  ${(timeDifference / totalEmployeesAnalyzed).toFixed(2)} min/employee

═══════════════════════════════════════════════════════════
📏 TOTAL DISTANCE COMPARISON (km):
═══════════════════════════════════════════════════════════
   PKW Baseline (nur Auto):     ${grandTotalPkwDistance.toFixed(2)} km
   With Best Alternatives:       ${grandTotalChosenDistance.toFixed(2)} km
   ⚡ Fastest Transports:         ${grandTotalFastestDistance.toFixed(2)} km
   ───────────────────────────────────────────────────────
   Best Alt. vs PKW:             ${distanceDifference.toFixed(2)} km (${distanceChangePercent}%)
   Fastest vs PKW:               ${fastestDistanceDiff.toFixed(2)} km (${fastestDistancePercent}%)
   Per-Employee Average (Best):  ${(distanceDifference / totalEmployeesAnalyzed).toFixed(2)} km/employee

═══════════════════════════════════════════════════════════
🌍 TOTAL CO2 COMPARISON (kg):
═══════════════════════════════════════════════════════════
   PKW Baseline (nur Auto):     ${grandTotalPkwCO2.toFixed(2)} kg
   With Best Alternatives:       ${grandTotalChosenCO2.toFixed(2)} kg
   ⚡ Fastest Transports:         ${grandTotalFastestCO2.toFixed(2)} kg
   ───────────────────────────────────────────────────────
   Best Alt. vs PKW:             ${co2Difference.toFixed(2)} kg (${co2ChangePercent}%)
   Fastest vs PKW:               ${fastestCO2Diff.toFixed(2)} kg (${fastestCO2Percent}%)
   Per-Employee Average (Best):  ${(co2Difference / totalEmployeesAnalyzed).toFixed(2)} kg/employee

═══════════════════════════════════════════════════════════
💡 Interpretation:
   ${timeDifference >= 0 ? "✅" : "⚠️"} Best Alternatives Time: ${Math.abs(timeDifference).toFixed(0)} minutes ${timeDifference >= 0 ? "saved" : "added"} (${Math.abs(parseFloat(timeChangePercent))}%)
   ${distanceDifference >= 0 ? "✅" : "⚠️"} Best Alternatives Distance: ${Math.abs(distanceDifference).toFixed(0)} km ${distanceDifference >= 0 ? "saved" : "added"} (${Math.abs(parseFloat(distanceChangePercent))}%)
   ${co2Difference >= 0 ? "✅" : "⚠️"} Best Alternatives CO2: ${Math.abs(co2Difference).toFixed(0)} kg ${co2Difference >= 0 ? "saved" : "added"} (${Math.abs(parseFloat(co2ChangePercent))}%)
   ⚡ Fastest Transports Time: ${Math.abs(fastestTimeDiff).toFixed(0)} minutes ${fastestTimeDiff >= 0 ? "saved" : "added"} (${Math.abs(parseFloat(fastestTimePercent))}%)
   ⚡ Fastest Transports Distance: ${Math.abs(fastestDistanceDiff).toFixed(0)} km ${fastestDistanceDiff >= 0 ? "saved" : "added"} (${Math.abs(parseFloat(fastestDistancePercent))}%)
   ⚡ Fastest Transports CO2: ${Math.abs(fastestCO2Diff).toFixed(0)} kg ${fastestCO2Diff >= 0 ? "saved" : "added"} (${Math.abs(parseFloat(fastestCO2Percent))}%)
═══════════════════════════════════════════════════════════
`);

        // ═══════════════════════════════════════════════════════════════
        // BUILD TABLE DATA: Group bestAlternativeResults by alternative transport distance
        // ═══════════════════════════════════════════════════════════════
        // NOTE: Grand totals (grandTotalChosenTime/Distance) already calculated above (lines 2414-2434)
        // We're only building the table data structure here, not recalculating totals

        for (const range of distanceRanges) {
          // Filter results by fixed PKW distance to keep group sizes stable
          // across all scenarios and filter variants.
          const resultsInRange = bestAlternativeResults.filter((result) => {
            const employeeData = routeData[result.address_id];
            if (!employeeData) return false;

            // Fixed baseline for grouping: PKW (Auto) distance.
            const pkwDistance = this.getDistanceForTransport(
              employeeData,
              "Auto",
            );

            if (pkwDistance === null) return false;

            const isWithinLowerBound = range.includeMin
              ? pkwDistance >= range.min
              : pkwDistance > range.min;
            return isWithinLowerBound && pkwDistance <= range.max;
          });

          // Count employees by transport
          const transportCounts: Record<string, number> = {};
          resultsInRange.forEach((result) => {
            const transport = result.recommended_transport;
            // Filter out current transport (PKW/Auto) since table only shows alternatives
            if (transport !== currentTransport) {
              transportCounts[transport] =
                (transportCounts[transport] || 0) + 1;
            }
          });

          // Apply allowedTransports filter (match existing logic)
          const allowedTransports = this.consideredAlternativeColumns.map(
            (transport) => transport.selector,
          );
          const filteredTransportCounts = Object.fromEntries(
            Object.entries(transportCounts).filter(([transport]) =>
              allowedTransports.includes(transport),
            ),
          ) as Record<string, number>;

          // Calculate totals
          const totalEmployees = resultsInRange.length;
          const employeesWithAlternatives = Object.values(
            filteredTransportCounts,
          ).reduce((sum: number, count: any) => sum + (count as number), 0);
          const employeesWithoutAlternative =
            totalEmployees - employeesWithAlternatives;

          // Build templateResult (required for table rendering)
          const templateResult = {
            distance_range: range.label,
            total_employees: totalEmployees,
            employees_without_alternative: employeesWithoutAlternative,
            total_with_alternatives: employeesWithAlternatives,
            success_percentage:
              totalEmployees > 0
                ? Math.round((employeesWithAlternatives / totalEmployees) * 100)
                : 0,
            transport_counts: filteredTransportCounts,
          };
          templateResults.push(templateResult);

          // Build analysisResult (for debugging/reference - legacy format)
          const mostCommonAlternative =
            Object.keys(filteredTransportCounts).length > 0
              ? Object.entries(filteredTransportCounts).reduce((a, b) =>
                  a[1] > b[1] ? a : b,
                )[0]
              : null;

          const analysisResult = {
            distance_range: range.label,
            employee_count: totalEmployees,
            best_alternative: mostCommonAlternative,
            avg_improvement: 0, // Can calculate if needed from result.improvement_percentage
            total_savings: 0, // Can calculate if needed
            success_rate:
              totalEmployees > 0
                ? Math.round((employeesWithAlternatives / totalEmployees) * 100)
                : 0,
            transport_counts: filteredTransportCounts,
            note:
              employeesWithAlternatives === 0
                ? "Keine Alternative erfüllt Kriterien"
                : employeesWithAlternatives === totalEmployees
                  ? "Alle können wechseln"
                  : "Teilweise geeignet",
          };
          analysisResults.push(analysisResult);

          // Debug logging
          // console.log(
          //   `✅ Range ${range.label}: ${resultsInRange.length} alternatives found`,
          // );
          // console.log(`   Transport breakdown:`, filteredTransportCounts);
        }

        console.log(
          `🎉 Unified analysis complete! Processed ${distanceRanges.length} ranges, API cache size: ${legCountCache.size}`,
        );
        return {
          analysisData: analysisResults,
          templateData: templateResults,
          success: true,
          cacheStats: {
            legCountCacheSize: legCountCache.size,
            rangesProcessed: distanceRanges.length,
          },
        };
      } catch (error) {
        console.error("💥 Critical error in unified analysis:", error);
        return {
          analysisData: [],
          templateData: [],
          success: false,
          error: error.message,
        };
      } finally {
        this.isCalculatingAnalysisData = false;
      }
    },

    // OPTIMIZED: Single function updates both data properties (eliminates duplicate processing)
    updateAllBestAlternativeData: async function () {
      // console.log(
      //   "🚀 updateAllBestAlternativeData: Single unified analysis for both outputs...",
      // );
      const result = await this.performBestAlternativeAnalysis();
      if (result.success) {
        // Update both data properties from single analysis
        this.bestAlternativeAnalysisResults = result.analysisData;
        this.templateStyleAnalysisResults = result.templateData;
        // console.log(
        //   `✅ BOTH datasets updated: ${result.analysisData.length} ranges, ${result.cacheStats.legCountCacheSize} cached API calls`,
        // );
      } else {
        // Clear both on failure
        this.bestAlternativeAnalysisResults = [];
        this.bestAlternativePointResults = [];
        this.templateStyleAnalysisResults = [];
        console.error("❌ Failed to calculate data:", result.error);
      }
    },

    // BACKWARD COMPATIBILITY: Legacy functions now delegate to optimized version
    calculateBestAlternativeAnalysisData: async function () {
      console.log(
        "📊 calculateBestAlternativeAnalysisData: Delegating to optimized unified function...",
      );
      await this.updateAllBestAlternativeData();
    },

    calculateTemplateStyleAnalysisData: async function () {
      console.log(
        "📋 calculateTemplateStyleAnalysisData: Delegating to optimized unified function...",
      );
      await this.updateAllBestAlternativeData();
    },

    prepareTableDataForExport() {
      // Get current comparison and metric titles
      const title = `${this.getMetricTitle} - ${this.selectedComparison}`;
      const firstMode = this.getFirstTransportMode;
      const secondMode = this.getSecondTransportMode;
      const ranges = this.getColumnRanges;

      // Prepare the data array for Excel
      const excelData = [];

      // Add title row
      excelData.push([title]);
      excelData.push([]); // Empty row for spacing

      // Add header rows (convert all values to strings)
      excelData.push(["", secondMode, ...ranges.map((r) => String(r))]);

      // Add data rows
      this.currentMatrix.forEach((row, rowIndex) => {
        if (rowIndex === 0) {
          excelData.push([
            firstMode,
            String(row.pkw),
            ...row.values.map((v) => (v === null ? "" : String(v))),
          ]);
        } else {
          excelData.push([
            "",
            String(row.pkw),
            ...row.values.map((v) => (v === null ? "" : String(v))),
          ]);
        }
      });

      // Add summary row
      excelData.push([]);
      excelData.push([
        `Neutral: ${String(this.neutralCount)}`,
        `Verbesserung: ${String(this.improvementCount)}`,
        `Verschlechterung: ${String(this.worseningCount)}`,
      ]);

      // Create styling information for each cell
      // Each row in styling matches a data row (starting from row 4 - the first data row)
      const styling = [];

      this.currentMatrix.forEach((row, rowIndex) => {
        const rowStyle = [];
        row.values.forEach((_, colIndex) => {
          let style = "default";
          // Determine style based on cell position
          if (this.isNeutralField(rowIndex, colIndex)) style = "neutral";
          else if (this.isGreenLightField(rowIndex, colIndex))
            style = "greenLight";
          else if (this.isGreenDarkField(rowIndex, colIndex))
            style = "greenDark";
          else if (this.isRedLightField(rowIndex, colIndex)) style = "redLight";
          else if (this.isRedDarkField(rowIndex, colIndex)) style = "redDark";
          rowStyle.push(style);
        });
        styling.push(rowStyle);
      });

      console.log("Prepared data:", {
        rows: excelData.length,
        styling: styling.length,
      });

      return {
        data: excelData,
        styling: styling,
      };
    },

    async downloadTable() {
      try {
        this.isDownloading = true;

        // Prepare the table data for export
        const exportData = this.prepareTableDataForExport();

        // Use the store to handle the download
        await this.cleverRouteImportStore.downloadTableAsExcel(exportData);
      } catch (error) {
        console.error("Error downloading table:", error);
        // You could add a notification here
      } finally {
        this.isDownloading = false;
      }
    },
    async fetchResults() {
      try {
        this.isLoading = true;

        // Clear computed values by resetting dependent data properties
        this.cleverRouteImportStore.cr_import_results = [];
        this.cleverRouteImportStore.uniqueAddresses = [];

        // Get current IDs from officeStore
        const projectId = this.officeStore.selectedProject.id;
        const companyId = this.officeStore.selectedCompany.id;
        const officeId = this.officeStore.selectedOffice.office_id;

        if (!projectId || !companyId || !officeId) {
          console.error("Missing required IDs from officeStore");
          return;
        }

        // First fetch path views which will populate uniqueAddresses
        await this.cleverRouteImportStore.fetchPathViews(
          projectId,
          companyId,
          officeId,
        );

        if (!this.uniqueAddresses?.length) {
          console.error("No unique addresses available");
          return;
        }

        // Sort addresses by origin_id to ensure we get correct first/last IDs
        const sortedAddresses = [...this.uniqueAddresses].sort(
          (a: any, b: any) => (a.origin_id || a.id) - (b.origin_id || b.id),
        );
        // console.log("sortedAddresses in fetchResults", sortedAddresses);

        const firstId = sortedAddresses[0].origin_id || sortedAddresses[0].id;
        const lastId =
          sortedAddresses[sortedAddresses.length - 1].origin_id ||
          sortedAddresses[sortedAddresses.length - 1].id;

        // Stauaufschläge vor den Ergebnissen laden: fetchCrImportResults
        // rechnet sie je nach Schalterstellung gleich mit ein.
        try {
          await this.plzSurchargeStore.fetchSurcharges(officeId);
        } catch (error) {
          // Kein Grund, die Ergebnisse zu verweigern - dann fehlt nur der
          // Schalter.
          console.error("Error loading PLZ surcharges:", error);
        }

        await this.cleverRouteImportStore.fetchCrImportResults(
          firstId,
          lastId,
          officeId,
        );
      } catch (error) {
        console.error("Error fetching results:", error);
        // Optionally add error handling/notification here
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Stauaufschlag ein- oder ausschalten.
     *
     * Ohne neue Abfrage: der Store hält die unveränderte Antwort und rechnet
     * den Aufschlag nur für die Anzeige ein.
     */
    onSurchargeToggle(value: boolean | null) {
      this.cleverRouteImportStore.setSurchargeEnabled(Boolean(value));
    },
    /**
     * Aufschlag eines Verkehrsmittels für die Anzeige hinter dem Zeitwert,
     * z. B. "97,1 (+30,1)". Liefert null, wenn es nichts auszuweisen gibt.
     */
    surchargeHint(record: any, prefix: string): number | null {
      if (!this.surchargeEnabled) return null;
      return surchargeFor(record, prefix);
    },
    getCellClass(rowIndex: number, colIndex: number): string {
      // Ensure we're only processing valid indices
      if (rowIndex >= 12 || colIndex >= 12) return "";

      // Calculate the difference between indices
      const diff = colIndex - rowIndex;

      // Apply coloring based on the difference
      if (Math.abs(diff) <= 1) {
        return "bg-neutral"; // Diagonal band (similar values)
      } else if (diff >= 2 && diff <= 3) {
        return "bg-red-light"; // Slightly worse
      } else if (diff >= 4) {
        return "bg-red-dark"; // Significantly worse
      } else if (diff <= -2 && diff >= -3) {
        return "bg-green-light"; // Slightly better
      } else if (diff <= -4) {
        return "bg-green-dark"; // Significantly better
      }

      return ""; // Fallback (should never reach here)
    },
    getDistanceIndex(distance: number): number {
      if (distance <= 5) return 0;
      if (distance <= 10) return 1;
      if (distance <= 15) return 2;
      if (distance <= 20) return 3;
      if (distance <= 30) return 4;
      if (distance <= 40) return 5;
      if (distance <= 50) return 6;
      if (distance <= 60) return 7;
      if (distance <= 80) return 8;
      if (distance <= 100) return 9;
      if (distance <= 120) return 10;
      return 11;
    },
    getTimeIndex(time: number): number {
      if (time <= 5) return 0;
      if (time <= 10) return 1;
      if (time <= 15) return 2;
      if (time <= 20) return 3;
      if (time <= 30) return 4;
      if (time <= 40) return 5;
      if (time <= 50) return 6;
      if (time <= 60) return 7;
      if (time <= 80) return 8;
      if (time <= 100) return 9;
      if (time <= 120) return 10;
      return 11;
    },
    getCO2Index(co2: number): number {
      if (co2 <= 0.5) return 0;
      if (co2 <= 1) return 1;
      if (co2 <= 2) return 2;
      if (co2 <= 3) return 3;
      if (co2 <= 4) return 4;
      if (co2 <= 6) return 5;
      if (co2 <= 8) return 6;
      if (co2 <= 10) return 7;
      if (co2 <= 12) return 8;
      if (co2 <= 14) return 9;
      if (co2 <= 15) return 10;
      return 11;
    },
    getCostIndex(cost: number): number {
      if (cost <= 2) return 0;
      else if (cost <= 4) return 1;
      else if (cost <= 6) return 2;
      else if (cost <= 8) return 3;
      else if (cost <= 10) return 4;
      else if (cost <= 12) return 5;
      else if (cost <= 14) return 6;
      else if (cost <= 16) return 7;
      else if (cost <= 18) return 8;
      else if (cost <= 20) return 9;
      else if (cost <= 25) return 10;
      else return 11;
    },

    getSportIndex(kcal: number): number {
      if (kcal <= 50) return 0;
      else if (kcal <= 100) return 1;
      else if (kcal <= 150) return 2;
      else if (kcal <= 200) return 3;
      else if (kcal <= 250) return 4;
      else if (kcal <= 300) return 5;
      else if (kcal <= 350) return 6;
      else if (kcal <= 400) return 7;
      else if (kcal <= 450) return 8;
      else if (kcal <= 500) return 9;
      else if (kcal <= 600) return 10;
      else return 11;
    },
    isNeutralField(rowIndex: number, colIndex: number): boolean {
      // Neutral if column index is within ±1 of row index (diagonal band)
      return Math.abs(colIndex - rowIndex) <= 1;
    },
    isGreenLightField(rowIndex: number, colIndex: number): boolean {
      // Green light if column index is 2-3 less than row index
      return rowIndex - colIndex >= 2 && rowIndex - colIndex <= 3;
    },
    isGreenDarkField(rowIndex: number, colIndex: number): boolean {
      // Green dark if column index is 4+ less than row index
      return rowIndex - colIndex >= 4;
    },
    isRedLightField(rowIndex: number, colIndex: number): boolean {
      // Red light if column index is 2-3 more than row index
      return colIndex - rowIndex >= 2 && colIndex - rowIndex <= 3;
    },
    isRedDarkField(rowIndex: number, colIndex: number): boolean {
      // Red dark if column index is 4+ more than row index
      return colIndex - rowIndex >= 4;
    },
    getDistanceColumnIndex(distance: number): number {
      if (distance <= 5) return 0;
      if (distance <= 10) return 1;
      if (distance <= 15) return 2;
      if (distance <= 20) return 3;
      if (distance <= 30) return 4;
      if (distance <= 40) return 5;
      if (distance <= 50) return 6;
      if (distance <= 60) return 7;
      if (distance <= 80) return 8;
      if (distance <= 100) return 9;
      if (distance <= 120) return 10;
      return 11;
    },
    //testDataPreparation() {
    //  const exportData = this.prepareTableDataForExport();
    //  console.log("Export Data:", exportData);
    //   Check if data and styling arrays are properly structured
    //  console.log("Data rows:", exportData.data.length);
    //  console.log("First row:", exportData.data[0]);
    //  console.log("Styling rows:", exportData.styling.length);
    //},
    getTransportModesFromComparison(comparison: string): [string, string] {
      // Default return value if parsing fails
      const defaultReturn: [string, string] = ["PKW", "PKW"];

      if (!comparison) {
        console.warn("Empty comparison string provided");
        return defaultReturn;
      }

      // Map UI display names to internal mode codes
      const modeMap = {
        Pkw: "PKW",
        ÖV: "OeV",
        "Bike&Ride": "Bike_and_Ride",
        "Park&Ride": "Park_and_Ride",
        Pedelec: "Pedelec",
        Fahrrad: "Fahrrad",
        "E-Auto": "E_Auto",
        Fußweg: "Fuss_Weg",
        Werksbus: "Werksbus",
        "Bike&Werksbus": "Bike_and_Werksbus",
        "P&R&B": "Park_Ride_and_Bike",
        "Bahnreakt.": "Bahnreakt_Ride",
      };

      // Split the comparison string (e.g., "Pkw vs. ÖV")
      const parts = comparison.split(" vs. ");

      if (parts.length !== 2) {
        console.warn(`Invalid comparison format: ${comparison}`);
        return defaultReturn;
      }

      const mode1 = modeMap[parts[0]];
      const mode2 = modeMap[parts[1]];

      if (!mode1 || !mode2) {
        console.warn(`Unknown transport mode in comparison: ${comparison}`);
        return defaultReturn;
      }

      return [mode1, mode2];
    },
    getTransportTypeFromMode(mode: string): string {
      // Map UI mode codes to database transport_type values
      const transportTypeMap = {
        PKW: "car",
        E_Auto: "car_electric",
        OeV: "train",
        Park_and_Ride: "park_and_ride",
        Bike_and_Ride: "bike_and_ride",
        Pedelec: "eBike",
        Fahrrad: "bike",
        Fuss_Weg: "walk",
        // BikeScooter: "bikescooter",
        // eScooter: "eScooter",
        Werksbus: "werksbus",
        Bike_and_Werksbus: "bike_and_werksbus",
        Park_Ride_and_Bike: "park_ride_and_bike",
        Bahnreakt_Ride: "bahnreaktivierung_and_ride",
      };

      return transportTypeMap[mode] || "";
    },
    getTransportModeRanges(mode: string): { min: number; max: number } {
      // Get parameters from the store
      const parameters = this.cleverRouteImportStore.parameters;

      if (!parameters || !Array.isArray(parameters)) {
        console.warn("Parameters not loaded yet or invalid format");
        return { min: 0, max: Infinity }; // Default fallback
      }

      // Get the transport_type for this mode
      const transportType = this.getTransportTypeFromMode(mode);

      if (!transportType) {
        console.warn(`Transport type mapping not found for mode: ${mode}`);
        return { min: 0, max: Infinity };
      }

      // Find the parameter entry for this transport type
      const paramEntry = parameters.find(
        (param) => param.transport_type === transportType,
      );

      if (!paramEntry) {
        console.warn(
          `Parameter entry not found for transport type: ${transportType}`,
        );
        return { min: 0, max: Infinity };
      }

      // Extract min and max ranges
      const minRange = parseFloat(paramEntry.range_min_km) || 0;
      const maxRange = parseFloat(paramEntry.range_max_km) || Infinity;

      return { min: minRange, max: maxRange };
    },
    getFilteredRecordsForComparison(): any[] {
      if (!this.crImportResults?.length) return [];

      // Get the transport modes being compared
      const [mode1, mode2] = this.getTransportModesFromComparison(
        this.selectedComparison,
      );

      // Get min and max distances for both modes from parameters
      const ranges1 = this.getTransportModeRanges(mode1);
      const ranges2 = this.getTransportModeRanges(mode2);
      // console.log("Ranges1:", ranges1);
      // console.log("Ranges2:", ranges2);

      // Map mode codes to distance field names
      const distanceFieldMap = {
        PKW: "PKW_Entfernung",
        E_Auto: "E_Auto_Entfernung",
        OeV: "OeV_Entfernung",
        Park_and_Ride: "Auto_OeV_Entfernung",
        Bike_and_Ride: "OeV_Fahrrad_Entfernung",
        Pedelec: "Pedelec_Entfernung",
        Fahrrad: "Fahrrad_Entfernung",
        Fuss_Weg: "Fuss_Weg_Entfernung",
        // BikeScooter: "BikeScooter_Entfernung",
        // eScooter: "eScooter_Entfernung",
        Werksbus: "Werksbus_Entfernung",
        Bike_and_Werksbus: "Fahrrad_Werksbus_Entfernung",
        Park_Ride_and_Bike: "Park_Ride_and_Bike_Entfernung",
        Bahnreakt_Ride: "Bahnreakt_Ride_Entfernung",
      };

      // Get the distance field names
      const distanceField1 = distanceFieldMap[mode1];
      // console.log("distanceField1: ", distanceField1);
      const distanceField2 = distanceFieldMap[mode2];
      // console.log("distanceField2: ", distanceField2);

      // Filter records based on both distance fields, considering min and max ranges
      return this.crImportResults.filter((record) => {
        const distance1 = parseFloat(record[distanceField1]) || 0;
        // console.log("distance1: ", distance1);
        const distance2 = parseFloat(record[distanceField2]) || 0;
        // console.log("distance2: ", distance2);

        // Check if distances are within both min and max ranges for both modes
        const isWithinRange1 =
          distance1 >= ranges1.min && distance1 <= ranges1.max;
        // console.log("isWithinRange1: ", isWithinRange1);
        const isWithinRange2 =
          distance2 >= ranges2.min && distance2 <= ranges2.max;
        //console.log("isWithinRange2: ", isWithinRange2);

        return (
          distance1 > 0 && distance2 > 0 && isWithinRange1 && isWithinRange2
        );
      });
    },
    getTransportRangeInfo(comparison: string) {
      const [mode1, mode2] = this.getTransportModesFromComparison(comparison);
      const ranges1 = this.getTransportModeRanges(mode1);
      const ranges2 = this.getTransportModeRanges(mode2);

      const modeName1 = comparison.split(" vs. ")[0];
      const modeName2 = comparison.split(" vs. ")[1];

      return `${modeName1}: ${ranges1.min}-${ranges1.max}km, ${modeName2}: ${ranges2.min}-${ranges2.max}km`;
    },

    async calculateBestAlternativeAnalysis() {
      this.isCalculatingBestAlternative = true;
      try {
        // Trigger recalculation by updating the flag
        this.bestAlternativeCalculated = false;

        // Small delay to show loading state
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Enable data display
        this.bestAlternativeCalculated = true;

        // Calculate BOTH analysis datasets with REAL database leg count validation (OPTIMIZED - single call!)
        await this.updateAllBestAlternativeData();

        // console.log(
        //   "🎉 Best Alternative analysis calculated with OPTIMIZED unified engine & REAL database leg count validation",
        // );
      } catch (error) {
        console.error("Error calculating Best Alternative analysis:", error);
      } finally {
        this.isCalculatingBestAlternative = false;
      }
    },

    // Best Alternative Analysis Helper Methods
    getDistanceForTransport(record: any, transport: string): number | null {
      const transportToField: { [key: string]: string } = {
        Auto: "PKW_Entfernung",
        "E-Auto": "E_Auto_Entfernung",
        ÖV: "OeV_Entfernung",
        Fahrrad: "Fahrrad_Entfernung",
        Pedelec: "Pedelec_Entfernung",
        "Zu Fuß": "Fuss_Weg_Entfernung",
        "Park & Ride": "Auto_OeV_Entfernung",
        "Bike & Ride": "OeV_Fahrrad_Entfernung",
        Werksbus: "Werksbus_Entfernung",
        "Bike & Werksbus": "Fahrrad_Werksbus_Entfernung",
        "Bahnreaktivierung & Ride": "Bahnreakt_Ride_Entfernung",
        "Park & Ride & Bike": "Park_Ride_and_Bike_Entfernung",
      };

      const fieldName = transportToField[transport];
      if (!fieldName) return null;

      const value = record[fieldName];
      return value !== null && value !== undefined ? value : null;
    },

    async findBestAlternativesForRange(
      employees: any[],
      currentTransport: string,
      metric: string,
      constraints: any,
    ) {
      // Only include transport modes we have data for
      const alternativeTransports = [
        "Auto",
        "E-Auto",
        "ÖV",
        "Fahrrad",
        "Pedelec",
        "Zu Fuß",
        "Park & Ride",
        "Bike & Ride",
        "Werksbus",
        "Bike & Werksbus",
        "Bahnreaktivierung & Ride",
        "Park & Ride & Bike",
      ].filter((t) => t !== currentTransport);

      const validAlternatives: any[] = [];
      let totalCurrentValue = 0;
      let totalAlternativeValue = 0;

      for (const employee of employees) {
        const currentValue = this.getMetricValueForTransport(
          employee,
          currentTransport,
          metric,
        );
        console.log("employee: ", employee);
        console.log("currentValue: ", currentValue);
        console.log("currentTransport: ", currentTransport);
        console.log("metric: ", metric);
        let bestAlternative = null;
        let bestValue = null;

        // Step 1: Collect all time-acceptable alternatives
        const acceptableAlternatives: any[] = [];

        for (const altTransport of alternativeTransports) {
          const altValue = this.getMetricValueForTransport(
            employee,
            altTransport,
            metric,
          );
          console.log("altValue: ", altValue);
          console.log("altTransport: ", altTransport);
          console.log("metric: ", metric);
          const timeConstraintsMet = this.checkTimeConstraints(
            employee,
            currentTransport,
            altTransport,
            constraints,
          );
          console.log("timeConstraintsMet: ", timeConstraintsMet);
          if (
            timeConstraintsMet &&
            altValue !== null &&
            altValue !== undefined
          ) {
            acceptableAlternatives.push({
              transport: altTransport,
              value: altValue,
              improvement: this.calculateImprovement(
                currentValue,
                altValue,
                metric,
              ),
            });
          }
        }

        // Step 2: Select best alternative based on constraint mode
        if (acceptableAlternatives.length === 0) {
          bestAlternative = null;
        } else if (!constraints.enforce_metric_constraints) {
          // Mode A: Find best alternative by metric value
          let bestValue = null;
          for (const alternative of acceptableAlternatives) {
            if (
              bestAlternative === null ||
              this.isValueBetter(alternative.value, bestValue, metric)
            ) {
              bestAlternative = alternative.transport;
              bestValue = alternative.value;
            }
          }
        } else {
          // Mode B: Find alternative closest to target improvement percentage
          let closestAlternative = null;
          let smallestDifference = Infinity;

          for (const alternative of acceptableAlternatives) {
            const difference = Math.abs(
              alternative.improvement -
                constraints.metric_improvement_percentage,
            );
            if (difference < smallestDifference) {
              smallestDifference = difference;
              closestAlternative = alternative.transport;
              bestValue = alternative.value;
            }
          }
          console.log("closestAlternative: ", closestAlternative);
          console.log("bestValue: ", bestValue);
          bestAlternative = closestAlternative;
        }

        // Step 3: Check leg constraints for public transport using DATABASE VALUES
        if (
          bestAlternative &&
          constraints.max_legs !== undefined &&
          constraints.max_legs !== null &&
          constraints.max_legs !== ""
        ) {
          const dbTransportName =
            this.mapFrontendToDbTransport(bestAlternative);
          console.log(
            `DB transport name in findBestAlternativesForRange: ${dbTransportName}`,
          );
          if (dbTransportName && this.isMultiLegTransport(dbTransportName)) {
            try {
              // Extract path_id from employee data - try multiple possible field names
              console.log(
                `Employee data in findBestAlternativesForRange: ${employee}`,
              );
              const pathId =
                employee.path_id || employee.PATH_ID || employee.id;
              console.log(`Path ID in findBestAlternativesForRange: ${pathId}`);
              if (pathId) {
                // Fetch ACTUAL leg count from database via API
                const actualLegCount = await this.mapInputStore.fetchLegCount(
                  pathId,
                  dbTransportName,
                );

                if (actualLegCount > parseInt(constraints.max_legs)) {
                  console.log(
                    `❌ ${bestAlternative} (${dbTransportName}) rejected: ${actualLegCount} legs > ${constraints.max_legs} max (REAL DB VALUE)`,
                  );
                  bestAlternative = null; // Fall back to current transport
                  bestValue = null;
                } else {
                  console.log(
                    `✅ ${bestAlternative} (${dbTransportName}) accepted: ${actualLegCount} legs <= ${constraints.max_legs} max (REAL DB VALUE)`,
                  );
                }
              } else {
                console.error(
                  `No path_id found for employee ${employee.MA_ID} - cannot validate leg count, rejecting alternative`,
                );
                // Reject the alternative if we can't get real data - NO ESTIMATION ALLOWED
                bestAlternative = null;
                bestValue = null;
              }
            } catch (error) {
              console.error(
                `Error fetching REAL leg count for ${bestAlternative}:`,
                error,
              );
              // On API error, reject the alternative - NO ESTIMATION FALLBACK ALLOWED
              bestAlternative = null;
              bestValue = null;
            }
          }
        }

        if (bestAlternative) {
          validAlternatives.push({
            employee,
            alternative: bestAlternative,
            currentValue,
            alternativeValue: bestValue,
            improvement: this.calculateImprovement(
              currentValue,
              bestValue,
              metric,
            ),
          });

          totalCurrentValue += currentValue;
          totalAlternativeValue += bestValue;
        }
      }

      const successRate = Math.round(
        (validAlternatives.length / employees.length) * 100,
      );
      const mostCommonAlternative =
        this.getMostCommonAlternative(validAlternatives);
      const avgImprovement =
        validAlternatives.length > 0
          ? validAlternatives.reduce((sum, v) => sum + v.improvement, 0) /
            validAlternatives.length
          : 0;
      const totalSavings = totalCurrentValue - totalAlternativeValue;

      // Calculate transport mode counts
      const transportCounts: { [key: string]: number } = {};
      validAlternatives.forEach((v) => {
        transportCounts[v.alternative] =
          (transportCounts[v.alternative] || 0) + 1;
      });

      return {
        mostCommonAlternative,
        avgImprovement,
        totalSavings,
        successRate,
        transportCounts,
        note:
          successRate === 0
            ? "Keine Alternative erfüllt Kriterien"
            : successRate === 100
              ? "Alle können wechseln"
              : "Teilweise geeignet",
      };
    },

    getMetricValueForTransport(
      record: any,
      transport: string,
      metric: string,
    ): number {
      const transportToFields: { [key: string]: { [key: string]: string } } = {
        Auto: {
          time: "PKW_Zeit",
          cost: "PKW_Kosten",
          co2: "PKW_CO2",
          sport: "PKW_Bewegung",
        },
        "E-Auto": {
          time: "E_Auto_Zeit",
          cost: "E_Auto_Kosten",
          co2: "E_Auto_CO2",
          sport: "E_Auto_Bewegung",
        },
        ÖV: {
          time: "OeV_Zeit",
          cost: "OeV_Kosten",
          co2: "OeV_CO2",
          sport: "OeV_Bewegung",
        },
        Fahrrad: {
          time: "Fahrrad_Zeit",
          cost: "Fahrrad_Kosten",
          co2: "Fahrrad_CO2",
          sport: "Fahrrad_Bewegung",
        },
        Pedelec: {
          time: "Pedelec_Zeit",
          cost: "Pedelec_Kosten",
          co2: "Pedelec_CO2",
          sport: "Pedelec_Bewegung",
        },
        "Zu Fuß": {
          time: "Fuss_Weg_Zeit",
          cost: "Fuss_Weg_Kosten",
          co2: "Fuss_Weg_CO2",
          sport: "Fuss_Weg_Bewegung",
        },
        Taxi: {
          time: "Taxi_Zeit",
          cost: "Taxi_Kosten",
          co2: "Taxi_CO2",
          sport: "Taxi_Bewegung",
        },
        "Park & Ride": {
          time: "Auto_OeV_Zeit",
          cost: "Auto_OeV_Kosten",
          co2: "Auto_OeV_CO2",
          sport: "Auto_OeV_Bewegung",
        },
        "Bike & Ride": {
          time: "OeV_Fahrrad_Zeit",
          cost: "OeV_Fahrrad_Kosten",
          co2: "OeV_Fahrrad_CO2",
          sport: "OeV_Fahrrad_Bewegung",
        },
        Werksbus: {
          time: "Werksbus_Zeit",
          cost: "Werksbus_Kosten",
          co2: "Werksbus_CO2",
          sport: "Werksbus_Bewegung",
        },
        "Bike & Werksbus": {
          time: "Fahrrad_Werksbus_Zeit",
          cost: "Fahrrad_Werksbus_Kosten",
          co2: "Fahrrad_Werksbus_CO2",
          sport: "Fahrrad_Werksbus_Bewegung",
        },
        "Bahnreaktivierung & Ride": {
          time: "Bahnreakt_Ride_Zeit",
          cost: "Bahnreakt_Ride_Kosten",
          co2: "Bahnreakt_Ride_CO2",
          sport: "Bahnreakt_Ride_Bewegung",
        },
        "Park & Ride & Bike": {
          time: "Park_Ride_and_Bike_Zeit",
          cost: "Park_Ride_and_Bike_Kosten",
          co2: "Park_Ride_and_Bike_CO2",
          sport: "Park_Ride_and_Bike_Bewegung",
        },
      };

      const fields = transportToFields[transport];
      const fieldName = fields ? fields[metric] : null;
      return fieldName ? record[fieldName] || 0 : 0;
    },

    checkTimeConstraints(
      record: any,
      currentTransport: string,
      altTransport: string,
      constraints: any,
    ): boolean {
      const currentTime = this.getMetricValueForTransport(
        record,
        currentTransport,
        "time",
      );
      const altTime = this.getMetricValueForTransport(
        record,
        altTransport,
        "time",
      );

      if (!currentTime || !altTime) return false;

      const timeIncrease = altTime - currentTime;
      const timePercentageIncrease = (timeIncrease / currentTime) * 100;

      return (
        timePercentageIncrease <= constraints.max_time_percentage &&
        timeIncrease <= constraints.max_additional_minutes
      );
    },

    checkImprovementConstraints(
      currentValue: number,
      altValue: number,
      metric: string,
      constraints: any,
    ): boolean {
      if (!currentValue || !altValue) return false;

      let improvement: number;
      if (metric === "sport") {
        improvement = ((altValue - currentValue) / currentValue) * 100;
      } else {
        improvement = ((currentValue - altValue) / currentValue) * 100;
      }

      return improvement >= constraints.metric_improvement_percentage;
    },

    isValueBetter(value1: number, value2: number, metric: string): boolean {
      if (value2 === null) return true;
      return metric === "sport" ? value1 > value2 : value1 < value2;
    },

    calculateImprovement(
      currentValue: number,
      altValue: number,
      metric: string,
    ): number {
      if (!currentValue) return 0;
      return metric === "sport"
        ? ((altValue - currentValue) / currentValue) * 100
        : ((currentValue - altValue) / currentValue) * 100;
    },

    /**
     * Map frontend transport names to database route_transport values
     * @param frontendTransport Frontend transport name (e.g., "ÖV", "Park & Ride")
     * @returns Database route_transport value (e.g., "train", "park_and_ride")
     */
    mapFrontendToDbTransport(frontendTransport: string): string | null {
      const mapping: { [key: string]: string } = {
        ÖV: "train",
        "Park & Ride": "park_and_ride",
        "Bike & Ride": "bike_and_ride",
        "Bahnreaktivierung & Ride": "bahnreaktivierung_and_ride",
        "Park & Ride & Bike": "park_ride_and_bike",
      };

      return mapping[frontendTransport] || null;
    },

    /**
     * Check if a transport mode has multiple legs (segments)
     * @param dbTransportName Database route_transport value
     * @returns true if transport typically has multiple legs
     */
    isMultiLegTransport(dbTransportName: string): boolean {
      return [
        "train",
        "park_and_ride",
        "bike_and_ride",
        "bahnreaktivierung_and_ride",
        "park_ride_and_bike",
      ].includes(dbTransportName);
    },

    getMostCommonAlternative(alternatives: any[]): string | null {
      if (alternatives.length === 0) return null;

      const counts: { [key: string]: number } = {};
      alternatives.forEach((alt) => {
        counts[alt.alternative] = (counts[alt.alternative] || 0) + 1;
      });

      return Object.entries(counts).reduce(
        (most: string | null, [alt, count]) =>
          count > (counts[most || ""] || 0) ? alt : most,
        null,
      );
    },

    getTransportIcon(transport: string): string {
      const iconMap: { [key: string]: string } = {
        Auto: "directions_car",
        "E-Auto": "electric_car",
        ÖV: "directions_bus",
        Fahrrad: "directions_bike",
        Pedelec: "electric_bike",
        "Zu Fuß": "directions_walk",
        Taxi: "local_taxi",
        "Park & Ride": "commute",
        "Bike & Ride": "pedal_bike",
        "e-Scooter": "electric_scooter",
        Carsharing: "car_rental",
        "Bahnreaktivierung & Ride": "directions_bus",
        "Park & Ride & Bike": "commute",
      };
      return iconMap[transport] || "help_outline";
    },

    getUnitForMetric(metric: string): string {
      const units: { [key: string]: string } = {
        time: "Minuten",
        cost: "Euro",
        co2: "kg CO₂",
        sport: "Schritte",
      };
      return units[metric] || "";
    },

    getMetricDisplayName(metric: string): string {
      const metricNames: { [key: string]: string } = {
        time: "Zeit (Minuten)",
        cost: "Kosten (Euro)",
        co2: "CO₂ (kg CO₂)",
        sport: "Sport (Schritte)",
        distance: "Distanz (km)",
      };
      return metricNames[metric] || metric;
    },

    formatGermanNumber(value: number): string {
      return value.toFixed(1).replace(".", ",");
    },
    formatComparisonMetricValue(value: number): string {
      const abs = Math.abs(value);
      const config = this.comparisonMetricConfig;
      const decimals = config.adaptivePrecision
        ? abs < 1
          ? 3
          : abs < 10
            ? 2
            : 1
        : config.precision;
      return value.toLocaleString("de-DE", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
    formatSignedComparisonMetricValue(value: number): string {
      if (value === 0) {
        return this.formatComparisonMetricValue(0);
      }
      const sign = value > 0 ? "+" : "-";
      return `${sign}${this.formatComparisonMetricValue(Math.abs(value))}`;
    },
    formatSignedPercentage(value: number): string {
      if (value === 0) {
        return this.formatGermanNumber(0);
      }
      const sign = value > 0 ? "+" : "-";
      return `${sign}${this.formatGermanNumber(Math.abs(value))}`;
    },
    isDifferenceImprovement(value: number): boolean {
      return this.comparisonMetricConfig.higherIsBetter
        ? value >= 0
        : value <= 0;
    },

    // Calculate percentage for individual transport modes
    getTransportPercentage(row: any, transport: string): number {
      if (!row.total_employees || row.total_employees === 0) return 0;
      const count = row.transport_counts[transport] || 0;
      return Math.round((count / row.total_employees) * 100);
    },

    // Format number with German locale (dot as thousands separator)
    formatNumber(value: number): string {
      if (value === 0) return "0";
      return value.toLocaleString("de-DE");
    },

    // Calculate percentage for "keine Alternative" column
    getNoAlternativePercentage(row: any): number {
      if (!row.total_employees || row.total_employees === 0) return 0;
      return Math.round(
        (row.employees_without_alternative / row.total_employees) * 100,
      );
    },

    // Get cell style with conditional green highlighting for alternatives
    getAlternativeCellStyle(count: number, total: number): string {
      if (total === 0 || count === 0) return "background-color: #f5f5f5";
      const percentage = (count / total) * 100;

      if (percentage >= 80) return "background-color: #2e7d32; color: white"; // Dark green
      if (percentage >= 50) return "background-color: #4caf50; color: white"; // Medium green
      if (percentage >= 20) return "background-color: #81c784"; // Light green
      if (percentage > 0) return "background-color: #c8e6c9"; // Very light green
      return "background-color: #f5f5f5";
    },

    // Get cell style for individual transport counts
    getTransportCellStyle(count: number, total: number): string {
      if (total === 0 || count === 0) return "background-color: #f5f5f5";
      const percentage = (count / total) * 100;

      if (percentage >= 50) return "background-color: #2e7d32; color: white"; // Dark green
      if (percentage >= 30) return "background-color: #4caf50; color: white"; // Medium green
      if (percentage >= 15) return "background-color: #81c784"; // Light green
      if (percentage > 0) return "background-color: #c8e6c9"; // Very light green
      return "background-color: #f5f5f5";
    },

    // Calculate total percentage for a transport type across all ranges
    getTotalTransportPercentage(transport: string): number {
      const total = this.totalSummary.total_employees;
      if (total === 0) return 0;
      const count = this.totalSummary.transport_totals[transport] || 0;
      return Math.round((count / total) * 100);
    },

    // Calculate combined percentage for all individual transport types
    getTotalIndividualPercentage(): number {
      const total = this.totalSummary.total_employees;
      if (total === 0) return 0;

      let sum = 0;
      for (const transport of this.consideredAlternativeColumns) {
        sum += this.totalSummary.transport_totals[transport.selector] || 0;
      }
      return Math.round((sum / total) * 100);
    },

    // Calculate bundled percentage for Fahrrad + Pedelec
    getBundledBikePercentage(): number {
      const total = this.totalSummary.total_employees;
      if (total === 0) return 0;

      const fahrrad = this.totalSummary.transport_totals["Fahrrad"] || 0;
      const pedelec = this.totalSummary.transport_totals["Pedelec"] || 0;
      return Math.round(((fahrrad + pedelec) / total) * 100);
    },

    // Calculate bundled percentage for ÖPNV + B&R + P&R
    getBundledPublicTransportPercentage(): number {
      const total = this.totalSummary.total_employees;
      if (total === 0) return 0;

      const oepnv = this.totalSummary.transport_totals["ÖV"] || 0;
      const bikeRide = this.totalSummary.transport_totals["Bike & Ride"] || 0;
      const parkRide = this.totalSummary.transport_totals["Park & Ride"] || 0;
      return Math.round(((oepnv + bikeRide + parkRide) / total) * 100);
    },

    // Export best alternative table to Excel with styling
    async exportBestAlternativeToExcel() {
      if (
        !this.templateStyleAnalysisResults ||
        this.templateStyleAnalysisResults.length === 0
      ) {
        return;
      }

      this.isExportingBestAlternative = true;

      try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Beste Alternative");

        // Define colors
        const lightRed = "FFFFEBEE";
        const lightGreen = "FFC8E6C9";
        const veryLightGreen = "FFC8E6C9";
        const mediumLightGreen = "FF81C784";
        const mediumGreen = "FF4CAF50";
        const darkGreen = "FF2E7D32";
        const headerGray = "FFF5F5F5";

        // Helper function to get fill color based on percentage
        const getAlternativeFillColor = (
          count: number,
          total: number,
        ): string => {
          if (total === 0 || count === 0) return headerGray;
          const percentage = (count / total) * 100;
          if (percentage >= 80) return darkGreen;
          if (percentage >= 50) return mediumGreen;
          if (percentage >= 20) return mediumLightGreen;
          if (percentage > 0) return veryLightGreen;
          return headerGray;
        };

        const getTransportFillColor = (
          count: number,
          total: number,
        ): string => {
          if (total === 0 || count === 0) return headerGray;
          const percentage = (count / total) * 100;
          if (percentage >= 50) return darkGreen;
          if (percentage >= 30) return mediumGreen;
          if (percentage >= 15) return mediumLightGreen;
          if (percentage > 0) return veryLightGreen;
          return headerGray;
        };

        const transportColumns = this.consideredAlternativeColumns;
        const transportSelectors = transportColumns.map(
          (transport) => transport.selector,
        );
        const transportDisplays = transportColumns.map(
          (transport) => transport.display,
        );

        // Set column widths
        worksheet.columns = Array.from(
          { length: 6 + transportColumns.length },
          () => ({ width: 10 }),
        );

        // Row 1: Main header groupings
        const headerRow1 = worksheet.addRow([
          this.criteriaSummaryText,
          "",
          "keine Alternative",
          "",
          "alle Alternativen",
          "",
          "Alternativen",
          ...Array(Math.max(transportColumns.length - 1, 0)).fill(""),
        ]);

        // Merge cells for header groupings
        worksheet.mergeCells("A1:B1");
        worksheet.mergeCells("C1:D1");
        worksheet.mergeCells("E1:F1");
        if (transportColumns.length > 0) {
          worksheet.mergeCells(1, 7, 1, 6 + transportColumns.length);
        }

        // Style header row 1
        headerRow1.eachCell((cell, colNumber) => {
          cell.font = { bold: true };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
          if (colNumber >= 1 && colNumber <= 2) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: headerGray },
            };
          } else if (colNumber >= 3 && colNumber <= 4) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: lightRed },
            };
          } else {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: lightGreen },
            };
          }
        });

        // Row 2: Sub-headers
        const headerRow2 = worksheet.addRow([
          "Radius",
          "gesamt",
          "absolut",
          "relativ",
          "absolut",
          "relativ",
          ...transportDisplays,
        ]);

        // Style header row 2
        headerRow2.eachCell((cell, colNumber) => {
          cell.font = { bold: true };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "medium" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
          if (colNumber >= 1 && colNumber <= 2) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: headerGray },
            };
          } else if (colNumber >= 3 && colNumber <= 4) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: lightRed },
            };
          } else {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: lightGreen },
            };
          }
        });

        // Data rows
        for (const row of this.templateStyleAnalysisResults) {
          const noAltPercentage =
            row.total_employees > 0
              ? Math.round(
                  (row.employees_without_alternative / row.total_employees) *
                    100,
                )
              : 0;

          const dataRow = worksheet.addRow([
            row.distance_range,
            row.total_employees,
            row.employees_without_alternative,
            `${noAltPercentage}%`,
            row.total_with_alternatives,
            `${row.success_percentage}%`,
            ...transportSelectors.map(
              (transport) => row.transport_counts[transport] || 0,
            ),
          ]);

          // Style data row
          dataRow.eachCell((cell, colNumber) => {
            cell.alignment = {
              horizontal: colNumber === 1 ? "center" : "right",
              vertical: "middle",
            };
            cell.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };

            // Apply conditional formatting
            if (colNumber >= 3 && colNumber <= 4) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: lightRed },
              };
            } else if (colNumber === 5 || colNumber === 6) {
              const fillColor = getAlternativeFillColor(
                row.total_with_alternatives,
                row.total_employees,
              );
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: fillColor },
              };
              if (fillColor === darkGreen || fillColor === mediumGreen) {
                cell.font = { color: { argb: "FFFFFFFF" } };
              }
            } else if (colNumber >= 7) {
              const transportIndex = colNumber - 7;
              const selector = transportSelectors[transportIndex];
              const count = selector ? row.transport_counts[selector] || 0 : 0;
              const fillColor = getTransportFillColor(
                count,
                row.total_employees,
              );
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: fillColor },
              };
              if (fillColor === darkGreen || fillColor === mediumGreen) {
                cell.font = { color: { argb: "FFFFFFFF" } };
              }
            }
          });
        }

        // Totals row (gesamt)
        const summary = this.totalSummary;
        const totalsRow = worksheet.addRow([
          "gesamt",
          summary.total_employees,
          summary.employees_without_alternative,
          `${summary.no_alternative_percentage}%`,
          summary.total_with_alternatives,
          `${summary.success_percentage}%`,
          ...transportSelectors.map(
            (transport) => summary.transport_totals[transport] || 0,
          ),
        ]);

        // Style totals row
        totalsRow.eachCell((cell, colNumber) => {
          cell.font = { bold: true };
          cell.alignment = {
            horizontal: colNumber === 1 ? "center" : "right",
            vertical: "middle",
          };
          cell.border = {
            top: { style: "medium" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: headerGray },
          };

          // Apply conditional formatting for totals
          if (colNumber >= 3 && colNumber <= 4) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: lightRed },
            };
          } else if (colNumber === 5 || colNumber === 6) {
            const fillColor = getAlternativeFillColor(
              summary.total_with_alternatives,
              summary.total_employees,
            );
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: fillColor },
            };
            if (fillColor === darkGreen || fillColor === mediumGreen) {
              cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            }
          } else if (colNumber >= 7) {
            const transportIndex = colNumber - 7;
            const selector = transportSelectors[transportIndex];
            const count = selector
              ? summary.transport_totals[selector] || 0
              : 0;
            const fillColor = getTransportFillColor(
              count,
              summary.total_employees,
            );
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: fillColor },
            };
            if (fillColor === darkGreen || fillColor === mediumGreen) {
              cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            }
          }
        });

        // Percentage summary row
        const percentRow = worksheet.addRow([
          "",
          "",
          "",
          "",
          "",
          "",
          ...transportSelectors.map(
            (transport) => `${this.getTotalTransportPercentage(transport)}%`,
          ),
        ]);

        percentRow.eachCell((cell) => {
          cell.alignment = { horizontal: "right", vertical: "middle" };
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
        });

        // Bundled percentages row
        const bundledRow = worksheet.addRow([
          "",
          "",
          "",
          "",
          `${summary.success_percentage}%`,
          "",
          ...transportSelectors.map(
            (transport) => `${this.getTotalTransportPercentage(transport)}%`,
          ),
        ]);

        // Merge cells for overall success percentage
        const bundledRowNum = bundledRow.number;
        worksheet.mergeCells(`E${bundledRowNum}:F${bundledRowNum}`);

        bundledRow.eachCell((cell) => {
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
        });

        // Generate filename with date
        const now = new Date();
        const dateStr = now.toISOString().split("T")[0];
        const filename = `beste_alternative_${dateStr}.xlsx`;

        // Generate and download the file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);

        console.log("Excel export completed successfully");
      } catch (error) {
        console.error("Error exporting to Excel:", error);
      } finally {
        this.isExportingBestAlternative = false;
      }
    },
    async exportBestAlternativeEmissionsToExcel() {
      if (
        !Array.isArray(this.templateStyleAnalysisResults) ||
        this.templateStyleAnalysisResults.length === 0
      ) {
        return;
      }

      this.isExportingBestAlternativeEmissions = true;

      try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Emissionen BA");

        const headerGray = "FFF5F5F5";
        const lightRed = "FFFFEBEE";
        const lightGreen = "FFC8E6C9";
        const darkGreen = "FF2E7D32";
        const darkRed = "FFB71C1C";

        worksheet.columns = [
          { width: 12 }, // Radius
          { width: 13 }, // Wohnorte
          { width: 10 }, // keine Alt absolut
          { width: 10 }, // keine Alt relativ
          { width: 17 }, // Basiswert
          { width: 20 }, // Gesamtwert
          { width: 18 }, // Beste Alternative
          { width: 18 }, // Keine Alternative
          { width: 12 }, // Diff absolut
          { width: 10 }, // Diff relativ
        ];

        const headerRow1 = worksheet.addRow([
          this.criteriaSummaryText,
          "",
          "keine Alternative",
          "",
          this.comparisonMetricGroupHeader,
          "",
          "",
          "",
          this.emissionsDifferenceGroupLabel,
          "",
        ]);
        worksheet.mergeCells("A1:B1");
        worksheet.mergeCells("C1:D1");
        worksheet.mergeCells("E1:H1");
        worksheet.mergeCells("I1:J1");

        headerRow1.eachCell((cell, colNumber) => {
          cell.font = { bold: true };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
          if (colNumber >= 1 && colNumber <= 2) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: headerGray },
            };
          } else if (colNumber >= 3 && colNumber <= 4) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: lightRed },
            };
          } else if (colNumber >= 5 && colNumber <= 8) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: lightGreen },
            };
          } else {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: headerGray },
            };
          }
        });

        const headerRow2 = worksheet.addRow([
          "Radius",
          "Wohnorte",
          "absolut",
          "relativ",
          this.emissionsBaselineSubLabel,
          "gesamt (mit/ohne Alternative)",
          "Beste Alternative",
          "keine Alternative",
          "absolut",
          "relativ",
        ]);
        headerRow2.eachCell((cell, colNumber) => {
          cell.font = { bold: true };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "medium" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
          if (colNumber >= 1 && colNumber <= 2) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: headerGray },
            };
          } else if (colNumber >= 3 && colNumber <= 4) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: lightRed },
            };
          } else if (colNumber >= 5 && colNumber <= 8) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: lightGreen },
            };
          } else {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: headerGray },
            };
          }
        });

        for (const row of this.emissionComparisonRows) {
          const diffIsImprovement = this.isDifferenceImprovement(
            row.difference_absolute,
          );
          const dataRow = worksheet.addRow([
            row.distance_range,
            row.total_employees,
            row.employees_without_alternative,
            `${row.no_alternative_percentage}%`,
            this.formatComparisonMetricValue(row.baseline_value),
            this.formatComparisonMetricValue(row.combined_value),
            this.formatComparisonMetricValue(row.best_alternative_value),
            this.formatComparisonMetricValue(row.no_best_alternative_value),
            this.formatSignedComparisonMetricValue(row.difference_absolute),
            `${this.formatSignedPercentage(row.difference_relative_percent)} %`,
          ]);

          dataRow.eachCell((cell, colNumber) => {
            cell.alignment = {
              horizontal: colNumber === 1 ? "center" : "right",
              vertical: "middle",
              wrapText: colNumber >= 5 && colNumber <= 8,
            };
            cell.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };
            if (colNumber >= 3 && colNumber <= 4) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: lightRed },
              };
            } else if (colNumber === 6) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: lightGreen },
              };
            }
            if (colNumber >= 9 && diffIsImprovement) {
              cell.font = { color: { argb: darkGreen } };
            } else if (colNumber >= 9) {
              cell.font = { color: { argb: darkRed } };
            }
          });
        }

        const totals = this.emissionComparisonTotals;
        const totalsDiffIsImprovement = this.isDifferenceImprovement(
          totals.difference_absolute,
        );
        const totalsRow = worksheet.addRow([
          "gesamt",
          totals.total_employees,
          totals.employees_without_alternative,
          `${totals.no_alternative_percentage}%`,
          this.formatComparisonMetricValue(totals.baseline_value),
          this.formatComparisonMetricValue(totals.combined_value),
          this.formatComparisonMetricValue(totals.best_alternative_value),
          this.formatComparisonMetricValue(totals.no_best_alternative_value),
          this.formatSignedComparisonMetricValue(totals.difference_absolute),
          `${this.formatSignedPercentage(totals.difference_relative_percent)} %`,
        ]);

        totalsRow.eachCell((cell, colNumber) => {
          cell.font = { bold: true };
          cell.alignment = {
            horizontal: colNumber === 1 ? "center" : "right",
            vertical: "middle",
          };
          cell.border = {
            top: { style: "medium" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: headerGray },
          };
          if (colNumber >= 9 && totalsDiffIsImprovement) {
            cell.font = { bold: true, color: { argb: darkGreen } };
          } else if (colNumber >= 9) {
            cell.font = { bold: true, color: { argb: darkRed } };
          }
        });

        const now = new Date();
        const dateStr = now.toISOString().split("T")[0];
        const metricCode = this.selectedComparisonMetric.toLowerCase();
        const filename = `beste_alternative_vergleich_${metricCode}_${dateStr}.xlsx`;

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting emissions table to Excel:", error);
      } finally {
        this.isExportingBestAlternativeEmissions = false;
      }
    },
    buildBestAlternativeGeoJsonFeatureCollection() {
      const pointResults = Array.isArray(this.bestAlternativePointResults)
        ? this.bestAlternativePointResults
        : [];
      if (pointResults.length === 0) {
        return {
          type: "FeatureCollection",
          features: [],
        };
      }

      const features = pointResults
        .filter((result: any) => {
          const coordinates = result?.geo?.coordinates;
          if (!Array.isArray(coordinates) || coordinates.length < 2) {
            return false;
          }

          const lng = Number(coordinates[0]);
          const lat = Number(coordinates[1]);
          return Number.isFinite(lng) && Number.isFinite(lat);
        })
        .map((result: any) => {
          const lng = Number(result.geo.coordinates[0]);
          const lat = Number(result.geo.coordinates[1]);

          return {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [lng, lat],
            },
            properties: {
              address_id: result.address_id,
              best_alternative_transport: result.recommended_transport,
              current_transport: result.current_transport,
              has_valid_alternative: result.meets_constraints,
              metric_type: result.metric_type,
            },
          };
        });

      return {
        type: "FeatureCollection",
        features,
      };
    },
    getBestAlternativeGeoJsonFileName() {
      const transportAbbreviations: Record<string, string> = {
        Auto: "A",
        "E-Auto": "EA",
        ÖV: "OEV",
        Fahrrad: "FB",
        Pedelec: "PE",
        "Zu Fuß": "ZF",
        "Park & Ride": "PR",
        "Bike & Ride": "BR",
        Werksbus: "WB",
        "Bike & Werksbus": "BWB",
        "Bahnreaktivierung & Ride": "BAR",
        "Park & Ride & Bike": "PRB",
      };
      const metricAbbreviations: Record<string, string> = {
        time: "TIME",
        cost: "COST",
        co2: "CO2",
        sport: "SPORT",
      };

      const encodeSignedNumber = (value: number | string | undefined) => {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return "NA";
        if (numericValue < 0) return `N${Math.abs(numericValue)}`;
        return `P${numericValue}`;
      };

      const currentTransportCode =
        transportAbbreviations[this.selectedBestAlternativeCurrentTransport] ||
        "UNK";
      const metricCode =
        metricAbbreviations[this.selectedBestAlternativeMetric] || "UNK";
      const timePercentCode = encodeSignedNumber(
        this.bestAlternativeConstraints.max_time_percentage,
      );
      const timeMinutesCode = encodeSignedNumber(
        this.bestAlternativeConstraints.max_additional_minutes,
      );
      const metricModeCode = this.bestAlternativeConstraints
        .enforce_metric_constraints
        ? `MOD-ZIEL${encodeSignedNumber(this.bestAlternativeConstraints.metric_improvement_percentage)}`
        : "MOD-BEST";
      const transfersCode =
        this.bestAlternativeConstraints.max_legs !== null &&
        this.bestAlternativeConstraints.max_legs !== undefined &&
        this.bestAlternativeConstraints.max_legs !== ""
          ? `UM-${this.bestAlternativeConstraints.max_legs}`
          : "UM-NA";

      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];

      return `beste_alt_punkte_VM-${currentTransportCode}_MET-${metricCode}_ZEITP-${timePercentCode}_ZEITM-${timeMinutesCode}_${metricModeCode}_${transfersCode}_${dateStr}.geojson`;
    },
    getBestAlternativeGeoJsonTooltip() {
      return "Dateiname enthält Filterkürzel: VM (Verkehrsmittel), MET (Metrik), ZEITP (%), ZEITM (Min), MOD (BEST/ZIEL), UM (Umstiege).";
    },
    async exportBestAlternativePointsGeoJson() {
      this.isExportingBestAlternativeGeoJson = true;

      try {
        const hasPointResults =
          Array.isArray(this.bestAlternativePointResults) &&
          this.bestAlternativePointResults.length > 0;
        if (!hasPointResults) {
          await this.updateAllBestAlternativeData();
        }

        const featureCollection =
          this.buildBestAlternativeGeoJsonFeatureCollection();
        if (
          !featureCollection.features ||
          featureCollection.features.length === 0
        ) {
          console.warn("No valid best-alternative point features to export.");
          return;
        }

        const filename = this.getBestAlternativeGeoJsonFileName();

        const blob = new Blob([JSON.stringify(featureCollection, null, 2)], {
          type: "application/geo+json;charset=utf-8",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(
          "Error exporting best alternative points as GeoJSON:",
          error,
        );
      } finally {
        this.isExportingBestAlternativeGeoJson = false;
      }
    },
  },
});
</script>

<style scoped>
.custom-wide-container {
  max-width: 1400px !important; /* or whatever width you prefer */
  margin: 0 auto;
  padding: 16px;
}

@media (max-width: 1200px) {
  .v-container {
    overflow-x: auto;
  }
}
.v-table {
  width: 100%;
  margin-top: 1rem;
}

th {
  background-color: #e8f5e9;
  text-align: center;
  padding: 8px;
}

.comparison-matrix-table {
  border-collapse: collapse;
  width: 100%;
}

.comparison-matrix-table :deep(tr) {
  height: 30px !important;
  min-height: 30px !important;
}

.comparison-matrix-table :deep(td),
.comparison-matrix-table :deep(th) {
  width: 120px;
  border: 1px solid #666;
  padding: 4px 8px;
  font-size: 1.2rem;
}

.comparison-matrix-table :deep(th):nth-child(n):nth-child(n + 1) {
  border-bottom: 1px solid #000;
}

.comparison-matrix-table :deep(th) {
  background-color: #f5f5f5;
  color: #1a237e;
  font-weight: bold;
  font-size: 1.2rem;
}
.comparison-matrix-table tbody tr {
  background-color: transparent !important;
}

.comparison-matrix-table tbody tr:nth-child(even),
.comparison-matrix-table tbody tr:nth-child(odd) {
  background-color: transparent !important;
}

.simple-distance-co2-table :deep(th) {
  text-align: center;
  width: 100%;
  font-size: 0.95rem;
}

.simple-distance-co2-table :deep(td) {
  width: 100%;
  font-size: 1.2rem;
}

.distance-distribution-table :deep(th),
.distance-distribution-table :deep(td) {
  font-size: 1.2rem;
}
.custom-table-width th:first-child,
.custom-table-width td:first-child {
  width: 165px; /* adjust to your desired width */
  max-width: 165px;
}
.custom-table-width th:nth-child(n + 2):nth-child(-n + 6),
.custom-table-width td:nth-child(n + 2):nth-child(-n + 6) {
  width: 200px; /* adjust to your desired width */
  max-width: 200px;
}

.optimal-transport-table th:first-child,
.optimal-transport-table td:first-child {
  width: 165px; /* adjust to your desired width */
  max-width: 165px;
}
.optimal-transport-table :deep(th),
.optimal-transport-table :deep(td) {
  font-size: 1.2rem;
}

td {
  text-align: center;
  padding: 8px;
}

.speed-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Add green background to the 5-10 km row */
tbody tr:nth-child(3) {
  background-color: rgba(232, 245, 233, 0.3);
}

.bg-neutral {
  background-color: #e0e0e0 !important;
  border-bottom: 1px solid #000 !important;
}
.bg-red-light {
  background-color: rgba(244, 67, 54, 0.2) !important;
  border-bottom: 1px solid #000 !important;
}
.bg-red-dark {
  background-color: rgba(244, 67, 54, 0.6) !important;
  border-bottom: 1px solid #000 !important;
}
.bg-green-light {
  background-color: rgba(76, 175, 80, 0.2) !important;
  border-bottom: 1px solid #000 !important;
}
.bg-green-dark {
  background-color: rgba(76, 175, 80, 0.6) !important;
  border-bottom: 1px solid #000 !important;
}
.rotated-header {
  height: 140px;
  white-space: nowrap;
  padding: 0 !important;
}

.rotated-text {
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  width: 30px;
  position: relative;
  left: 25px; /* Fine-tune positioning */
  text-align: center;
  font-size: 1.2rem;
}

.table-actions {
  margin-bottom: 1rem;
  margin-top: 1rem;
  display: flex;
  gap: 1rem;
}

.larger-select-text :deep(.v-field__input) {
  font-size: 1.2rem; /* Adjust size as needed */
}

.larger-select-text :deep(.v-list-item-title) {
  font-size: 1.2rem; /* Adjust size as needed */
}

.larger-select-text :deep(.v-messages) {
  margin-top: 6px; /* Reduce space between label and hint */
}

/* Full width table styling for best alternative */
.full-width-table {
  width: 100% !important;
  table-layout: fixed;
}

.full-width-table :deep(table) {
  width: 100% !important;
  margin: 0 !important;
}

.full-width-table :deep(th),
.full-width-table :deep(td) {
  padding: 10px 6px !important;
  font-size: 0.95rem;
  word-wrap: break-word;
  text-align: center;
}

.full-width-table :deep(th) {
  font-weight: bold;
}

/* Ensure container takes full width */
.v-container {
  max-width: 100% !important;
  padding: 0 16px;
}

.best-alternative-combined-table {
  width: 100% !important;
  table-layout: auto;
  font-size: 0.9rem;
}

.best-alternative-combined-table :deep(th) {
  padding: 12px 6px !important;
  font-size: 0.9rem !important;
  word-break: break-word;
}

.best-alternative-combined-table :deep(td) {
  padding: 8px 6px !important;
  font-size: 0.9rem !important;
}

/* Force equal column widths for transport columns */
.best-alternative-combined-table :deep(th:nth-child(n + 4)),
.best-alternative-combined-table :deep(td:nth-child(n + 4)) {
  width: auto !important;
  min-width: 0 !important;
  max-width: none !important;
}
</style>
